import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format, parse } from 'date-fns';
import { es } from 'date-fns/locale';
import { generateReservationsPDF, generateAdminTablePDF } from '../utils/pdfGenerator';
import { Loader2, FileUp } from "lucide-react";
import { History, CalendarDays } from "lucide-react";
import html2canvas from 'html2canvas-pro';
import jsPDF from 'jspdf';

// 🔌 Deriva la URL del WebSocket a partir de la URL del API (http->ws, https->wss),
// quitando el sufijo "/api" porque el WS vive en la raíz del backend (/ws/reservations).
const getWsUrl = () => {
  try {
    const httpBase = API.replace(/\/api\/?$/, '');
    return httpBase.replace(/^http/, 'ws') + '/ws/reservations';
  } catch {
    return null;
  }
};

const NIVEL_OPTIONS = ['Inicial', 'Primaria', 'Secundaria', 'Directivo', 'Aip'];

// Un docente puede trabajar en más de un nivel; se guarda como "Inicial,Primaria"
const parseNiveles = (nivelStr) =>
  (nivelStr || '').split(',').map((n) => n.trim()).filter(Boolean);

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [userSortField, setUserSortField] = useState('nombre'); // 'nombre' | 'nivel'
  const [userSortDirection, setUserSortDirection] = useState('asc'); // 'asc' | 'desc'
  const [reservations, setReservations] = useState([]);
  const [detailReservation, setDetailReservation] = useState(null);
  const [reservationsEnabled, setReservationsEnabled] = useState(true);
  const [topTeachersVisible, setTopTeachersVisible] = useState(true);
  const [attendanceMode, setAttendanceMode] = useState('manual'); // 'manual' | 'qr'
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [motivo, setMotivo] = useState("");
  const [toggleLoading, setToggleLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  // 🎓 Nivel del informe en PDF: 'Todos' (los 3 niveles en un solo PDF, como antes)
  // o 'Inicial' / 'Primaria' / 'Secundaria' (un PDF solo de ese nivel).
  const [selectedReportLevel, setSelectedReportLevel] = useState('Todos');
  // 🗓️ Año seleccionado para el informe en PDF. Antes el filtro solo miraba
  // el número de mes, así que "Marzo" traía marzo de TODOS los años con
  // datos. Ahora primero se elige el año y luego los meses de ESE año.
  const [selectedReportYear, setSelectedReportYear] = useState(new Date().getFullYear());
  const [showPastReservations, setShowPastReservations] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [confirmWord, setConfirmWord] = useState('');
  // 🗑️ Eliminar UNA reserva: id pendiente de confirmar + palabra escrita ("eliminar")
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [deleteReservationWord, setDeleteReservationWord] = useState('');
  const [formData, setFormData] = useState({
    dni: '',
    nombres: '',
    apellidos: '',
    email: '',
    telefono: '',
    nivel: '',
  });
  const [monthlyReports, setMonthlyReports] = useState([]);
  const [loadingMonthlyReports, setLoadingMonthlyReports] = useState(false);
  const [generatingReportNow, setGeneratingReportNow] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const axiosConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  useEffect(() => {
    if (!token) {
      toast.error('Sesión expirada');
      navigate('/admin-login');
    }
  }, [token, navigate]);

  // 🔒 Igual que en el panel del docente: si el token guardado deja de ser
  // válido mientras el admin sigue navegando (SIN haber cerrado sesión), el
  // backend responde 401 "Token inválido" en cualquier petición. Se detecta
  // acá, se limpia la sesión guardada y se regresa automáticamente al login.
  useEffect(() => {
    sessionStorage.removeItem('admin_session_expired_redirect');

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          const alreadyRedirecting = sessionStorage.getItem('admin_session_expired_redirect');
          localStorage.removeItem('token');
          if (!alreadyRedirecting) {
            sessionStorage.setItem('admin_session_expired_redirect', '1');
            toast.error('Tu sesión expiró o el token ya no es válido. Vuelve a iniciar sesión.');
            navigate('/admin-login');
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [navigate]);

  const resetForm = () => {
    setFormData({
      dni: '',
      nombres: '',
      apellidos: '',
      email: '',
      telefono: '',
      nivel: '',
    });
    setIsEditing(false);
  };

  // ✅ Un docente puede trabajar en varios niveles (checkboxes -> string "Inicial,Primaria")
  const toggleNivelInFormData = (nivelValue) => {
    setFormData((prev) => {
      const actuales = parseNiveles(prev.nivel);
      const yaEsta = actuales.includes(nivelValue);
      const nuevos = yaEsta
        ? actuales.filter((n) => n !== nivelValue)
        : [...actuales, nivelValue];
      return { ...prev, nivel: nuevos.join(',') };
    });
  };

  const loadUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, axiosConfig);
      setUsers(response.data || []);
    } catch (error) {
      toast.error('No se pudieron cargar los docentes');
    }
  }, [axiosConfig]);

  const loadReservations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reservations/all`, axiosConfig);
      setReservations(Array.isArray(response.data) ? response.data : response.data.reservations || response.data.data || []);
    } catch (error) {
      toast.error('No se pudieron cargar las reservas');
      setReservations([]);
    }
  }, [axiosConfig]);

  const loadSystemConfig = useCallback(async () => {
    try {
      const statusRes = await axios.get(`${API}/system/reservations-status`);
      setReservationsEnabled(statusRes.data.enabled);

      const motivoRes = await axios.get(`${API}/system/reservations-motivo`);
      setMotivo(motivoRes.data.motivo || "");

      const topRes = await axios.get(`${API}/system/top-teachers-visibility`, axiosConfig);
      setTopTeachersVisible(topRes.data.visible !== false);

      const modeRes = await axios.get(`${API}/system/attendance-mode`, axiosConfig);
      setAttendanceMode(modeRes.data.mode === 'qr' ? 'qr' : 'manual');

      const remindersRes = await axios.get(`${API}/system/reminders-status`, axiosConfig);
      setRemindersEnabled(remindersRes.data.enabled !== false);
    } catch (error) {
      setReservationsEnabled(true);
      setMotivo("");
      setTopTeachersVisible(true);
      setAttendanceMode('manual');
      setRemindersEnabled(true);
    }
  }, [axiosConfig]);

  useEffect(() => {
    if (token) {
      loadUsers();
      loadReservations();
      loadSystemConfig();
      loadMonthlyReports();
      loadAnnualTrends();
      loadAuditLogs();
    }
    // Se excluyen a propósito loadMonthlyReports/loadAnnualTrends/loadAuditLogs:
    // solo deben cargarse UNA VEZ al montar (cuando aparece el token), no cada
    // vez que cambia su identidad (por ejemplo, loadAnnualTrends cambia cuando
    // el admin elige otro año en el filtro de tendencias, y eso no debe volver
    // a disparar la carga de usuarios/reservas/config).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loadUsers, loadReservations, loadSystemConfig]);

  //actualizaciones en tiempo real de nuevas reservas, al administrador.
  useEffect(() => {
  if (!token) return;

  const interval = setInterval(() => {
    loadReservations();
  }, 5000);

  return () => clearInterval(interval);
}, [token, loadReservations]);

  // 📅 Recordatorio: reservas de HOY del turno ACTUAL, de todos los docentes,
  // para que el admin vea de un vistazo lo que falta atender en el AIP ahora.
  // ⏱️ Se actualiza cada minuto para que el turno y las reservas ya terminadas
  // se vayan quitando solas, sin necesidad de recargar la página.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNowTick(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Turno mañana: 07:30 - 12:30. Turno tarde: desde 13:00. Antes de la 1pm
  // se considera turno mañana (incluye el receso del mediodía).
  const currentShift = useMemo(() => {
    const nowMinutes = nowTick.getHours() * 60 + nowTick.getMinutes();
    return nowMinutes < 13 * 60 ? "mañana" : "tarde";
  }, [nowTick]);

  const todaysPendingReservations = useMemo(() => {
    const todayStr = format(nowTick, "yyyy-MM-dd");
    const nowMinutes = nowTick.getHours() * 60 + nowTick.getMinutes();

    return (reservations || [])
      .filter((r) => {
        if (r.date !== todayStr || r.shift !== currentShift) return false;
        // Se deja de mostrar apenas termina el bloque (no solo por fecha)
        const [, endStr] = (r.time_slot || "").split(" - ").map((s) => s.trim());
        if (!endStr) return true;
        const [endH, endM] = endStr.split(":").map(Number);
        const endMinutes = endH * 60 + endM;
        return nowMinutes < endMinutes;
      })
      .sort((a, b) => (a.time_slot || "").localeCompare(b.time_slot || ""));
  }, [reservations, nowTick, currentShift]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataFile = new FormData();
    formDataFile.append('file', file);

    try {
      setLoading(true);
      const res = await axios.post(`${API}/admin/users/upload`, formDataFile, {
        headers: {
          ...axiosConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(res.data.message || 'Usuarios cargados desde Excel');
      loadUsers();
    } catch (error) {
      let errorMsg = 'Error al subir Excel';
      const detail = error.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') errorMsg = detail;
        else if (Array.isArray(detail)) errorMsg = detail.map(e => e.msg).join('; ');
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const userFormRef = useRef(null);

  const handleEdit = (user) => {
    setIsEditing(true);
    setFormData({
      dni: user.dni,
      nombres: user.nombres,
      apellidos: user.apellidos,
      email: user.email || '',
      telefono: user.telefono || '',
      nivel: user.nivel,
    });
    userFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCreateOrUpdateUser = async (e) => {
    e.preventDefault();
    if (!formData.dni || !formData.nombres || !formData.apellidos) {
      toast.error('DNI, nombres y apellidos son obligatorios');
      return;
    }
    if (parseNiveles(formData.nivel).length === 0) {
      toast.error('Selecciona al menos un nivel');
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/admin/users/${formData.dni}`, formData, axiosConfig);
        toast.success('Docente actualizado');
      } else {
        await axios.post(`${API}/admin/users`, formData, axiosConfig);
        toast.success('Docente registrado');
      }
      resetForm();
      loadUsers();
    } catch (error) {
      let errorMsg = 'Error al guardar docente';
      const detail = error.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') errorMsg = detail;
        else if (Array.isArray(detail)) errorMsg = detail.map(e => e.msg).join('; ');
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (dni) => {
    if (!window.confirm('¿Eliminar permanentemente a este docente?')) return;

    try {
      await axios.delete(`${API}/admin/users/${dni}`, axiosConfig);
      toast.success('Docente eliminado');
      loadUsers();
    } catch (error) {
      toast.error('Error al eliminar docente');
    }
  };

  const handleToggleReservations = async () => {
    if (toggleLoading) return;

    setToggleLoading(true);
    try {
      const newEnabled = !reservationsEnabled;
      const res = await axios.post(`${API}/system/update`, {
        enabled: newEnabled,
        motivo: motivo
      }, axiosConfig);
      setReservationsEnabled(newEnabled);
      toast.success(res.data.message || `Reservas ${newEnabled ? 'activadas' : 'desactivadas'}`);
    } catch (error) {
      toast.error('Error al cambiar el estado');
    } finally {
      setToggleLoading(false);
    }
  };

  const [topTeachersToggleLoading, setTopTeachersToggleLoading] = useState(false);
  const handleToggleTopTeachers = async () => {
    if (topTeachersToggleLoading) return;

    setTopTeachersToggleLoading(true);
    try {
      const newVisible = !topTeachersVisible;
      const res = await axios.post(`${API}/system/update`, {
        top_teachers_visible: newVisible,
      }, axiosConfig);
      setTopTeachersVisible(newVisible);
      toast.success(res.data.message || `Top 5 ${newVisible ? 'visible' : 'oculto'} para docentes`);
    } catch (error) {
      toast.error('Error al cambiar la visibilidad del Top 5');
    } finally {
      setTopTeachersToggleLoading(false);
    }
  };

  const [attendanceModeLoading, setAttendanceModeLoading] = useState(false);

  const [remindersToggleLoading, setRemindersToggleLoading] = useState(false);
  const handleToggleReminders = async () => {
    if (remindersToggleLoading) return;

    setRemindersToggleLoading(true);
    try {
      const newEnabled = !remindersEnabled;
      const res = await axios.post(`${API}/system/update`, {
        reminders_enabled: newEnabled,
      }, axiosConfig);
      setRemindersEnabled(newEnabled);
      toast.success(res.data.message || `Recordatorios ${newEnabled ? 'activados' : 'desactivados'}`);
    } catch (error) {
      toast.error('Error al cambiar los recordatorios');
    } finally {
      setRemindersToggleLoading(false);
    }
  };

  const handleSetAttendanceMode = async (mode) => {
    if (attendanceModeLoading || mode === attendanceMode) return;

    setAttendanceModeLoading(true);
    try {
      const res = await axios.post(`${API}/system/update`, {
        attendance_mode: mode,
      }, axiosConfig);
      setAttendanceMode(mode);
      toast.success(res.data.message || `Modo de asistencia: ${mode === 'qr' ? 'Check-in QR' : 'Manual'}`);
    } catch (error) {
      toast.error('Error al cambiar el modo de asistencia');
    } finally {
      setAttendanceModeLoading(false);
    }
  };

  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const handleExportExcel = async () => {
    setIsExportingExcel(true);
    try {
      const res = await axios.get(`${API}/admin/export-excel`, {
        ...axiosConfig,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Reservas_AIP_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado correctamente');
    } catch (error) {
      toast.error('Error al exportar a Excel');
    } finally {
      setIsExportingExcel(false);
    }
  };

  const [isDownloadingQrPdf, setIsDownloadingQrPdf] = useState(false);

  // 📎 Descarga de TODOS los adjuntos de una reserva en un ZIP. Dentro del ZIP va una
  // carpeta con el nombre del docente y los datos de la reserva (fecha, horario,
  // grado/sección, área), así los archivos de distintas reservas no se confunden.
  // El backend BORRA los archivos del servidor apenas termina de enviarlos
  // (almacenamiento temporal), así que después ya no estarán disponibles.
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);
  const handleDownloadReservationAttachments = async (reservation) => {
    const reservationId = reservation.id || reservation._id;
    setDownloadingAttachmentId(reservationId);
    try {
      const res = await axios.get(
        `${API}/admin/reservations/${reservationId}/attachments-zip`,
        { ...axiosConfig, responseType: 'blob' }
      );

      // Nombre de la carpeta/ZIP que arma el servidor (viene en Content-Disposition)
      let fileName = `Adjuntos - ${reservation.user_name || 'docente'}.zip`;
      const disposition = res.headers?.['content-disposition'] || '';
      const utf8Match = disposition.match(/filename\*=utf-8''([^;]+)/i);
      const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
      try {
        if (utf8Match) fileName = decodeURIComponent(utf8Match[1]);
        else if (plainMatch) fileName = plainMatch[1];
      } catch (e) { /* se usa el nombre por defecto */ }

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Carpeta descargada (ZIP). Al descomprimirla verás los archivos dentro de la carpeta de la reserva. Se eliminaron del servidor.');
      loadReservations();
    } catch (error) {
      toast.error('No se pudo descargar los adjuntos (puede que ya hayan sido descargados antes)');
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  // 🔒 Código secreto que hace que el QR sea real (sin esto, el backend
  // rechaza cualquier check-in, así nadie puede marcar asistencia sin
  // escanear el código físico pegado en la sala del AIP).
  const [checkinSecret, setCheckinSecret] = useState(null);
  const [isRegeneratingCode, setIsRegeneratingCode] = useState(false);

  const loadCheckinSecret = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/checkin-secret`, axiosConfig);
      setCheckinSecret(res.data?.code || null);
    } catch (error) {
      setCheckinSecret(null);
    }
  }, [axiosConfig]);

  useEffect(() => {
    if (attendanceMode === 'qr') {
      loadCheckinSecret();
    }
  }, [attendanceMode, loadCheckinSecret]);

  const handleRegenerateCheckinCode = async () => {
    if (!window.confirm('¿Regenerar el código? Los QR ya impresos dejarán de funcionar y tendrás que imprimir uno nuevo.')) {
      return;
    }
    setIsRegeneratingCode(true);
    try {
      const res = await axios.post(`${API}/admin/checkin-secret/regenerate`, {}, axiosConfig);
      setCheckinSecret(res.data?.code || null);
      toast.success('Código regenerado. Descarga e imprime el nuevo QR.');
    } catch (error) {
      toast.error('Error al regenerar el código');
    } finally {
      setIsRegeneratingCode(false);
    }
  };
  const handleDownloadCheckinQrPdf = async (checkinUrl) => {
    setIsDownloadingQrPdf(true);
    try {
      const res = await axios.get(`${API}/admin/checkin-qr-pdf`, {
        ...axiosConfig,
        params: { url: checkinUrl },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Registro_Asistencia_QR.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado correctamente');
    } catch (error) {
      toast.error('Error al generar el PDF del QR');
    } finally {
      setIsDownloadingQrPdf(false);
    }
  };

  // ✅ Bitácora de auditoría
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [auditLogsError, setAuditLogsError] = useState('');
  const [auditFilterAdmin, setAuditFilterAdmin] = useState('');
  const [auditFilterDateFrom, setAuditFilterDateFrom] = useState('');
  const [auditFilterDateTo, setAuditFilterDateTo] = useState('');
  // Refs "espejo" del filtro: permiten que loadAuditLogs lea el valor más
  // reciente sin que su identidad cambie en cada tecla — así el WebSocket
  // (que depende de loadAuditLogs) no se reconecta mientras escribes.
  const auditFiltersRef = useRef({ admin: '', dateFrom: '', dateTo: '' });
  useEffect(() => {
    auditFiltersRef.current = {
      admin: auditFilterAdmin,
      dateFrom: auditFilterDateFrom,
      dateTo: auditFilterDateTo,
    };
  }, [auditFilterAdmin, auditFilterDateFrom, auditFilterDateTo]);

  const loadAuditLogs = useCallback(async () => {
    setLoadingAuditLogs(true);
    setAuditLogsError('');
    try {
      const { admin, dateFrom, dateTo } = auditFiltersRef.current;
      const params = {};
      if (admin.trim()) params.admin_username = admin.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      console.log('[Auditoría] pidiendo con params:', params);
      const res = await axios.get(`${API}/admin/audit-log`, { ...axiosConfig, params });
      console.log('[Auditoría] respuesta:', res.data);
      setAuditLogs(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('[Auditoría] error al cargar:', error.response?.status, error.response?.data || error.message);
      setAuditLogs([]);
      setAuditLogsError(
        error.response
          ? `Error ${error.response.status}: ${JSON.stringify(error.response.data)}`
          : 'No se pudo conectar con el servidor.'
      );
    } finally {
      setLoadingAuditLogs(false);
    }
  }, [axiosConfig]);

  // ✅ Tendencia anual de reservas
  const [annualTrends, setAnnualTrends] = useState(null);
  const [annualTrendsYear, setAnnualTrendsYear] = useState(new Date().getFullYear());
  const loadAnnualTrends = useCallback(async (year) => {
    try {
      const res = await axios.get(`${API}/admin/annual-trends`, {
        ...axiosConfig,
        params: { year: year || annualTrendsYear },
      });
      setAnnualTrends(res.data);
    } catch (error) {
      setAnnualTrends(null);
    }
  }, [axiosConfig, annualTrendsYear]);

  // ✅ Descargar PDF: Tendencia Anual de Reservas (año actual vs año anterior, mes a mes)
  const handleDownloadAnnualTrendsPDF = () => {
    if (!annualTrends || !annualTrends.months?.length) {
      toast.info('Todavía no hay datos de tendencia anual para exportar.');
      return;
    }
    const rows = annualTrends.months.map((m) => [
      m.label,
      String(m.current),
      String(m.previous),
    ]);
    generateSimpleListPDF(
      'Tendencia Anual de Reservas',
      `${annualTrends.year} (Total: ${annualTrends.total_current}) vs ${annualTrends.previous_year} (Total: ${annualTrends.total_previous})`,
      ['Mes', `Reservas ${annualTrends.year}`, `Reservas ${annualTrends.previous_year}`],
      rows,
      [80, 50, 50],
      `Tendencia_Anual_${annualTrends.year}.pdf`
    );
  };

  const handleDeleteAllReservations = async () => {
    if (!window.confirm('¿Estás seguro de ELIMINAR TODAS LAS RESERVAS? Esta acción no se puede deshacer.')) return;

    try {
      await axios.post(`${API}/admin/delete-all-reservations`, {}, axiosConfig);
      toast.success('Todas las reservas eliminadas');
      loadReservations();
    } catch (error) {
      toast.error('Error al eliminar reservas');
    }
  };

  // Abre el cuadro de confirmación (hay que escribir "eliminar").
  // El administrador puede eliminar cualquier reserva, también las pasadas.
  const handleDeleteReservation = (id) => {
    setDeleteReservationWord('');
    setReservationToDelete(id);
  };

  const closeDeleteReservationModal = () => {
    setReservationToDelete(null);
    setDeleteReservationWord('');
  };

  const confirmDeleteReservation = async () => {
    if (!reservationToDelete) return;
    if (deleteReservationWord.trim().toLowerCase() !== 'eliminar') return;
    const id = reservationToDelete;
    closeDeleteReservationModal();

    try {
      await axios.delete(`${API}/reservations/${id}`, axiosConfig);
      toast.success('Reserva eliminada');
      loadReservations();
    } catch (error) {
      toast.error('Error al eliminar reserva');
    }
  };

  // ✅ Marcar / desmarcar inasistencia de una reserva
  const handleToggleAttendance = async (reservation) => {
    const id = reservation.id || reservation._id;
    const nuevoValor = !(reservation.asistio ?? true);
    const confirmMsg = nuevoValor
      ? '¿Marcar esta reserva como ASISTIÓ?'
      : '¿Marcar esta reserva como NO ASISTIÓ?';
    if (!window.confirm(confirmMsg)) return;

    try {
      await axios.put(
        `${API}/reservations/${id}/asistencia`,
        { asistio: nuevoValor },
        axiosConfig
      );
      toast.success(nuevoValor ? 'Marcado como asistió' : 'Marcado como no asistió');
      loadReservations();
    } catch (error) {
      toast.error('Error al actualizar la asistencia');
    }
  };

  // ✅ Marca directamente un valor (true/false) — usado cuando la reserva
  // está "pendiente" y el admin elige explícitamente una de las dos opciones.
  const handleSetAttendance = async (reservation, valor) => {
    const id = reservation.id || reservation._id;
    const confirmMsg = valor
      ? '¿Marcar esta reserva como ASISTIÓ?'
      : '¿Marcar esta reserva como NO ASISTIÓ?';
    if (!window.confirm(confirmMsg)) return;

    try {
      await axios.put(
        `${API}/reservations/${id}/asistencia`,
        { asistio: valor },
        axiosConfig
      );
      toast.success(valor ? 'Marcado como asistió' : 'Marcado como no asistió');
      loadReservations();
    } catch (error) {
      toast.error('Error al actualizar la asistencia');
    }
  };

  // ✅ Total de reservas por docente (histórico completo): asistió / no asistió / total
  const attendanceStatsByDni = useMemo(() => {
    const stats = {};
    reservations.forEach(r => {
      const dni = r.user_dni;
      if (!dni) return;
      if (!stats[dni]) {
        stats[dni] = { total: 0, asistio: 0, noAsistio: 0 };
      }
      stats[dni].total += 1;
      if (r.asistio === false) {
        stats[dni].noAsistio += 1;
      } else if (r.asistio === true) {
        stats[dni].asistio += 1;
      }
    });
    return stats;
  }, [reservations]);

  // ✅ Total de reservas por docente, SOLO del mes actual: asistió / no asistió / total
  const monthlyAttendanceStatsByDni = useMemo(() => {
    const now = new Date();
    const nowMonth = now.getMonth();
    const nowYear = now.getFullYear();
    const stats = {};
    reservations.forEach(r => {
      const dni = r.user_dni;
      if (!dni || !r.date) return;
      const resDate = new Date(r.date + 'T00:00:00');
      if (resDate.getMonth() !== nowMonth || resDate.getFullYear() !== nowYear) return;
      if (!stats[dni]) {
        stats[dni] = { total: 0, asistio: 0, noAsistio: 0 };
      }
      stats[dni].total += 1;
      if (r.asistio === false) {
        stats[dni].noAsistio += 1;
      } else if (r.asistio === true) {
        stats[dni].asistio += 1;
      }
    });
    return stats;
  }, [reservations]);

  // ✅ Búsqueda de docentes por DNI o nombre/apellido
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // ✅ Búsqueda de reservas por DNI/nombre del docente y por fecha
  const [reservationSearchQuery, setReservationSearchQuery] = useState('');
  const [reservationSearchDate, setReservationSearchDate] = useState('');
  const [reservationSearchShift, setReservationSearchShift] = useState(''); // '' = todos, 'mañana', 'tarde'

  // ✅ Docentes ordenados por Nombre o Nivel, ascendente/descendente
  // (ya filtrados por la búsqueda de DNI/nombre, si hay alguna)
  const sortedUsers = useMemo(() => {
    const query = userSearchQuery.trim().toLowerCase();
    const list = query
      ? users.filter((u) => {
          const dni = (u.dni || '').toLowerCase();
          const nombreCompleto = `${u.nombres || ''} ${u.apellidos || ''}`.toLowerCase();
          return dni.includes(query) || nombreCompleto.includes(query);
        })
      : [...users];
    list.sort((a, b) => {
      let valA, valB;
      if (userSortField === 'nivel') {
        valA = parseNiveles(a.nivel).join(', ');
        valB = parseNiveles(b.nivel).join(', ');
      } else {
        valA = `${a.nombres || ''} ${a.apellidos || ''}`.trim();
        valB = `${b.nombres || ''} ${b.apellidos || ''}`.trim();
      }
      const cmp = valA.localeCompare(valB, 'es', { sensitivity: 'base' });
      return userSortDirection === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [users, userSortField, userSortDirection, userSearchQuery]);

  const handleSortUsers = (field) => {
    if (userSortField === field) {
      setUserSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setUserSortField(field);
      setUserSortDirection('asc');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Sesión cerrada');
    navigate('/');
  };

  const getMonthName = (monthNum) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[monthNum - 1];
  };

  const isReservationPast = (reservation) => {
    if (!reservation.date || !reservation.shift) return false;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();

    const [yearStr, monthStr, dayStr] = reservation.date.split('-');
    const resYear = parseInt(yearStr);
    const resMonth = parseInt(monthStr) - 1;
    const resDay = parseInt(dayStr);

    if (resYear < currentYear) return true;
    if (resYear === currentYear && resMonth < currentMonth) return true;
    if (resYear === currentYear && resMonth === currentMonth && resDay < currentDay) return true;
    if (!(resYear === currentYear && resMonth === currentMonth && resDay === currentDay)) {
      return false; // fecha futura
    }

    // Es HOY: la reserva pasa a "pasada" apenas termina SU PROPIO bloque
    // (ej. si reservó 09:00 - 09:45, pasa a las 09:45, no al final del turno).
    const endTimeStr = (reservation.time_slot || '').split(' - ')[1]?.trim();
    if (!endTimeStr) return false; // sin horario reconocible: no asumir nada
    const [endHourStr, endMinuteStr] = endTimeStr.split(':');
    const endHour = parseInt(endHourStr);
    const endMinute = parseInt(endMinuteStr);

    if (currentHours > endHour) return true;
    if (currentHours === endHour && currentMinutes >= endMinute) return true;
    return false;
  };

  // 🗓️ Años que tienen al menos una reserva, de más reciente a más antiguo.
  // Sirve para poblar el selector de año del modal de descarga.
  const availableReportYears = useMemo(() => {
    const years = new Set(
      reservations
        .filter(r => r.date)
        .map(r => new Date(r.date).getFullYear())
    );
    const currentYear = new Date().getFullYear();
    years.add(currentYear); // siempre disponible, aunque aún no tenga reservas
    return Array.from(years).sort((a, b) => b - a);
  }, [reservations]);

  // Meses (1-12) que SÍ tienen alguna reserva dentro del año dado.
  const getMonthsWithDataForYear = useCallback((year, level = selectedReportLevel) => {
    return new Set(
      reservations
        .filter(r => level === 'Todos' || (r.nivel || '').trim() === level)
        .filter(r => r.date && new Date(r.date).getFullYear() === Number(year))
        .map(r => new Date(r.date).getMonth() + 1)
        .filter(m => m >= 1 && m <= 12)
    );
  }, [reservations, selectedReportLevel]);

  const handleDownloadReport = () => {
    if (reservations.length === 0) {
      toast.warning('No hay reservas para descargar');
      return;
    }
    // Por defecto se abre en el año más reciente que tenga reservas
    // (o el año actual si no hay ninguna reserva todavía).
    const defaultYear = availableReportYears.find(
      (y) => getMonthsWithDataForYear(y, 'Todos').size > 0
    ) ?? availableReportYears[0];
    const monthsWithReservations = getMonthsWithDataForYear(defaultYear, 'Todos');
    setSelectedReportLevel('Todos');
    setSelectedReportYear(defaultYear);
    setSelectedMonths(monthsWithReservations);
    setSelectAll(monthsWithReservations.size === 12);
    setIsDownloadModalOpen(true);
  };

  // Cambiar de año dentro del modal: se reinicia la selección de meses a los
  // que sí tienen datos en el año recién elegido.
  const handleSelectReportYear = (year) => {
    const y = Number(year);
    setSelectedReportYear(y);
    const monthsWithReservations = getMonthsWithDataForYear(y);
    setSelectedMonths(monthsWithReservations);
    setSelectAll(monthsWithReservations.size === 12);
  };

  // Cambiar de nivel dentro del modal: se recalculan los meses con datos de ESE nivel.
  const handleSelectReportLevel = (level) => {
    setSelectedReportLevel(level);
    const monthsWithReservations = getMonthsWithDataForYear(selectedReportYear, level);
    setSelectedMonths(monthsWithReservations);
    setSelectAll(monthsWithReservations.size === 12);
  };

  // ✅ Referencia al contenedor completo de la pestaña Reportes
  const reportSnapshotRef = useRef(null);
  const [isGeneratingSnapshot, setIsGeneratingSnapshot] = useState(false);

  // ✅ Descarga TODA la pestaña Reportes (tarjetas, gráficos, top docentes, etc.) tal como se ve en pantalla
  const handleDownloadReportSnapshot = async () => {
    if (!reportSnapshotRef.current) return;
    setIsGeneratingSnapshot(true);
    try {
      const canvas = await html2canvas(reportSnapshotRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#f5e6d3',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Reportes_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (error) {
      console.error('Error al generar el PDF de la pestaña Reportes:', error);
      toast.error('Error al generar el PDF. Intenta nuevamente.');
    } finally {
      setIsGeneratingSnapshot(false);
    }
  };

  // ✅ Reportes mensuales históricos (generados automáticamente por el servidor)
  const MESES_NOMBRES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  const loadMonthlyReports = useCallback(async () => {
    setLoadingMonthlyReports(true);
    try {
      const res = await axios.get(`${API}/admin/monthly-reports`, axiosConfig);
      setMonthlyReports(res.data || []);
    } catch (error) {
      console.error('Error al cargar reportes mensuales:', error);
    } finally {
      setLoadingMonthlyReports(false);
    }
  }, [axiosConfig]);

  // 🔌 Sincronización en tiempo real: escucha los eventos que emite el
  // backend por WebSocket (usuarios, reservas, configuración, auditoría y
  // reportes) y refresca solo lo que cambió, sin necesidad de recargar la
  // página ni cerrar/abrir sesión. Se reconecta solo si la conexión se cae.
  const wsRef = useRef(null);
  useEffect(() => {
    if (!token) return undefined;

    let reconnectTimer = null;
    let closedByUs = false;

    const connect = () => {
      const wsUrl = getWsUrl();
      console.log('[WS admin] intentando conectar a:', wsUrl, '(API =', API, ')');
      if (!wsUrl) return;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS admin] conectado a', wsUrl);
      };

      ws.onmessage = (evt) => {
        let msg;
        try {
          msg = JSON.parse(evt.data);
        } catch {
          return;
        }
        console.log('[WS admin] evento recibido:', msg);
        switch (msg.event) {
          case 'reservation_created':
          case 'reservation_deleted':
          case 'reservation_updated':
          case 'reservation_attendance_updated':
          case 'reservations_changed':
            loadReservations();
            break;
          case 'users_changed':
          case 'user_status_updated':
            loadUsers();
            break;
          case 'config_updated':
            loadSystemConfig();
            break;
          case 'audit_log':
            loadAuditLogs();
            break;
          case 'reports_changed':
            loadMonthlyReports();
            break;
          default:
            break;
        }
      };

      ws.onclose = (evt) => {
        console.log('[WS admin] desconectado. code:', evt.code, 'reason:', evt.reason);
        if (!closedByUs) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };

      ws.onerror = (err) => {
        console.error('[WS admin] error de conexión:', err);
        ws.close();
      };
    };

    connect();

    return () => {
      closedByUs = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [token, loadReservations, loadUsers, loadSystemConfig, loadAuditLogs, loadMonthlyReports]);

  const handleDownloadMonthlyReport = async (report) => {
    try {
      const res = await axios.get(
        `${API}/admin/monthly-reports/${report.id}/download`,
        { ...axiosConfig, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Reporte Mensual ${MESES_NOMBRES[report.month - 1]} ${report.year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('No se pudo descargar el reporte');
    }
  };

  const handleGenerateReportNow = async () => {
    setGeneratingReportNow(true);
    try {
      await axios.post(`${API}/admin/monthly-reports/generate`, {}, axiosConfig);
      toast.success('Reporte del mes actual generado correctamente');
      await loadMonthlyReports();
    } catch (error) {
      toast.error('Error al generar el reporte');
    } finally {
      setGeneratingReportNow(false);
    }
  };
  
  const closeDownloadModal = () => {
    setIsDownloadModalOpen(false);
    setSelectedMonths(new Set());
    setSelectAll(false);
    setSelectedReportLevel('Todos');
  };

  const toggleMonth = (monthNum) => {
    const month = Number(monthNum);
    const newSelected = new Set(selectedMonths);
    if (newSelected.has(month)) {
      newSelected.delete(month);
    } else {
      newSelected.add(month);
    }
    setSelectedMonths(newSelected);
    setSelectAll(newSelected.size === 12);
  };

  const toggleSelectAll = () => {
    const monthsWithDataThisYear = getMonthsWithDataForYear(selectedReportYear);
    if (selectAll) {
      setSelectedMonths(new Set());
    } else {
      setSelectedMonths(monthsWithDataThisYear);
    }
    setSelectAll(!selectAll);
  };

  const confirmDownloadReport = () => {
    if (selectedMonths.size === 0) {
      toast.warning('Selecciona al menos un mes');
      return;
    }

    const selectedMonthNumbers = Array.from(selectedMonths).map(m => Number(m));
    const filtered = reservations.filter(r => {
      if (!r.date) return false;
      // r.date viene como "YYYY-MM-DD": se separa como texto. Con new Date(r.date)
      // se interpreta en UTC y, en Perú (UTC-5), el día 1 de un mes caía en el
      // mes anterior (y el día 1 del mes siguiente caía en este), alterando
      // el total del informe.
      const [resYear, resMonth] = r.date.split('-').map(Number);
      if (resYear !== Number(selectedReportYear)) return false;
      if (selectedReportLevel !== 'Todos' && (r.nivel || '').trim() !== selectedReportLevel) return false;
      return selectedMonthNumbers.includes(resMonth);
    });

    if (filtered.length === 0) {
      const monthNames = selectedMonthNumbers.map(m => getMonthName(m)).join(', ');
      toast.warning(`No hay reservas${selectedReportLevel !== 'Todos' ? ` de nivel ${selectedReportLevel}` : ''} en: ${monthNames} ${selectedReportYear}`);
      return;
    }

    try {
      generateReservationsPDF(filtered, null, selectedMonthNumbers, selectedReportYear, selectedReportLevel === 'Todos' ? null : selectedReportLevel);
      closeDownloadModal();
    } catch (error) {
      console.error('Error al generar PDF:', error);
      toast.error('Error al generar el documento. Intenta nuevamente.');
    }
  };

  // 🕒 "Mes actual" en horario de Perú, calculado sin depender del huso
  // horario del navegador ni del parseo UTC de `new Date("YYYY-MM-DD")`
  // (que puede correr la fecha un día hacia atrás/adelante cerca del cambio
  // de mes y hacer que se cuenten reservas del mes anterior, o se pierdan
  // reservas del mes actual). "r.date" se compara como texto "YYYY-MM"
  // directamente, y el "hoy" se obtiene formateando la hora real en la zona
  // 'America/Lima'.
  const peruNowParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Lima',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  const currentYear = Number(peruNowParts.find(p => p.type === 'year').value);
  const currentMonth = Number(peruNowParts.find(p => p.type === 'month').value); // 1-12
  const currentYearMonthPrefix = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
  // Objeto Date (día 1 del mes actual en Perú) solo para mostrar la etiqueta
  // "Mes Año" en el encabezado del reporte — no se usa para filtrar.
  const currentDate = new Date(currentYear, currentMonth - 1, 1);

  const monthlyReservations = reservations.filter(r => {
    if (!r.date) return false;
    // r.date siempre viene como "YYYY-MM-DD": comparar como texto evita
    // cualquier conversión de zona horaria.
    return r.date.startsWith(currentYearMonthPrefix);
  });

  const totalMonthly = monthlyReservations.length;
  const morningMonthly = monthlyReservations.filter(r => r.shift === 'mañana').length;
  const afternoonMonthly = monthlyReservations.filter(r => r.shift === 'tarde').length;

  const levels = ['Inicial', 'Primaria', 'Secundaria'];
  const reservationsByLevel = {};
  levels.forEach(level => {
    reservationsByLevel[level] = monthlyReservations.filter(r => r.nivel === level).length;
  });

  let maxLevel = 'Ninguno';
  let maxCount = 0;
  levels.forEach(level => {
    if (reservationsByLevel[level] > maxCount) {
      maxCount = reservationsByLevel[level];
      maxLevel = level;
    }
  });

  // ⚠️ Antes se comparaba por nombre concatenado (`${nombres} ${apellidos}`
  // vs r.user_name), lo cual es frágil: cualquier diferencia de espacios,
  // tildes, o si el nombre del docente se editó después de reservar, hacía
  // que NO coincidiera y el docente apareciera como "inactivo" aunque sí
  // había reservado. Se corrige comparando por DNI, que es el identificador
  // único y estable de cada docente.
  const usersWithReservations = new Set(
    monthlyReservations.map(r => r.user_dni).filter(Boolean)
  );
  const inactiveTeachers = users.filter(user => {
    const yaReservo = usersWithReservations.has(user.dni);
    const esDirectivoOAip = parseNiveles(user.nivel).some((n) =>
      ["directivo", "aip"].includes(n.trim().toLowerCase())
    );
    return !yaReservo && !esDirectivoOAip;
  });

// [NUEVO] Lógica para obtener el TOP 5 Docentes con más reservas, POR NIVEL
  // Solo se cuentan las reservas con asistencia CONFIRMADA (asistio === true)
  // Y que además ya hayan ocurrido (mismo criterio que la etiqueta
  // "Pendiente" que se ve en la tabla: isReservationPast). Esto evita que una
  // reserva marcada como asistida por adelantado (ej. check-in QR antes de
  // tiempo) para una fecha futura -que en pantalla se sigue viendo como
  // "Pendiente"- se cuente en el ranking antes de tiempo.
  const teacherCountsByLevel = { Inicial: {}, Primaria: {}, Secundaria: {} };
  // 🔧 Se normaliza el nivel de cada reserva (trim + minúsculas) antes de
  // compararlo contra los 3 niveles reales. Esto asegura que las reservas de
  // docentes con más de un nivel (cuyo "nivel" se elige por reserva) SIEMPRE
  // se contabilicen en el nivel que realmente reservaron, aunque el valor
  // guardado tenga espacios extra o distinta capitalización.
  const normalizeNivel = (n) => {
    const match = levels.find((lvl) => lvl.toLowerCase() === (n || '').trim().toLowerCase());
    return match || null;
  };
  monthlyReservations.forEach(r => {
    const nivelNormalizado = normalizeNivel(r.nivel);
    if (r.user_name && nivelNormalizado && r.asistio === true && isReservationPast(r)) {
      teacherCountsByLevel[nivelNormalizado][r.user_name] =
        (teacherCountsByLevel[nivelNormalizado][r.user_name] || 0) + 1;
    }
  });

  const topTeachersByLevel = {};
  levels.forEach(level => {
    topTeachersByLevel[level] = Object.entries(teacherCountsByLevel[level])
      .sort((a, b) => b[1] - a[1]) // Ordenar de mayor a menor
      .slice(0, 5); // Tomar solo los 5 primeros
  });

  // ✅ Descargar PDF: Top 5 Docentes Activos (los 3 niveles combinados en una sola tabla)
  const handleDownloadTopTeachersPDF = () => {
    const hasAnyData = levels.some((level) => topTeachersByLevel[level].length > 0);
    if (!hasAnyData) {
      toast.info('Todavía no hay datos suficientes del Top 5 para exportar.');
      return;
    }
    const monthLabel = format(new Date(), "MMMM yyyy", { locale: es });
    const rows = [];
    levels.forEach((level) => {
      topTeachersByLevel[level].forEach(([name, count], idx) => {
        rows.push([level, `${idx + 1}º`, name, String(count)]);
      });
    });
    generateSimpleListPDF(
      'Top 5 Docentes Activos',
      `Mes: ${monthLabel} — Ranking por nivel (Inicial, Primaria, Secundaria)`,
      ['Nivel', 'Puesto', 'Docente', 'Reservas'],
      rows,
      [45, 25, 80, 30],
      `Top5_Docentes_${format(new Date(), 'yyyy-MM')}.pdf`
    );
  };

  // [NUEVO] Docentes con inasistencias este mes: cuántas veces reservaron y no asistieron
  const noShowCounts = {};
  monthlyReservations.forEach(r => {
    if (r.asistio === false && r.user_name) {
      noShowCounts[r.user_name] = (noShowCounts[r.user_name] || 0) + 1;
    }
  });
  const noShowTeachers = Object.entries(noShowCounts)
    .sort((a, b) => b[1] - a[1]); // Ordenar de mayor a menor cantidad de inasistencias
  const totalNoShows = Object.values(noShowCounts).reduce((sum, n) => sum + n, 0);

  // ✅ Genera un PDF A4 simple tipo lista (encabezado + tabla con paginación
  // automática), reutilizable para cualquier reporte de docentes en lista.
  const generateSimpleListPDF = (title, subtitle, headers, rows, colWidths, filename) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginX = 15;
    const usableWidth = pageWidth - marginX * 2;
    const rowHeight = 8;
    let y;

    const drawHeader = () => {
      pdf.setFillColor(26, 77, 46); // #1a4d2e
      pdf.rect(0, 0, pageWidth, 28, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(15);
      pdf.text(title, marginX, 14);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(subtitle, marginX, 21);
      pdf.setFontSize(8);
      pdf.text(`Generado: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, pageWidth - marginX, 21, { align: 'right' });
    };

    const drawTableHeader = () => {
      pdf.setFillColor(26, 77, 46);
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.rect(marginX, y, usableWidth, rowHeight, 'F');
      let x = marginX;
      headers.forEach((h, i) => {
        pdf.text(h, x + 2, y + rowHeight - 2.5);
        x += colWidths[i];
      });
      y += rowHeight;
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
    };

    drawHeader();
    y = 38;
    drawTableHeader();

    rows.forEach((row, idx) => {
      if (y + rowHeight > pageHeight - 15) {
        pdf.addPage();
        y = 20;
        drawTableHeader();
      }
      if (idx % 2 === 1) {
        pdf.setFillColor(245, 230, 211); // #f5e6d3
        pdf.rect(marginX, y, usableWidth, rowHeight, 'F');
      }
      let x = marginX;
      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);
      row.forEach((cell, i) => {
        pdf.text(String(cell ?? ''), x + 2, y + rowHeight - 2.5);
        x += colWidths[i];
      });
      y += rowHeight;
    });

    pdf.save(filename);
  };

  // ✅ Descargar PDF: Docentes que NO ingresaron al AIP este mes
  const handleDownloadInactivePDF = () => {
    if (inactiveTeachers.length === 0) {
      toast.info('No hay docentes inactivos este mes para exportar.');
      return;
    }
    const monthLabel = format(new Date(), "MMMM yyyy", { locale: es });
    const rows = inactiveTeachers.map((u) => [
      `${u.nombres} ${u.apellidos}`,
      parseNiveles(u.nivel).join(', '),
      u.dni,
    ]);
    generateSimpleListPDF(
      'Docentes que NO ingresaron al AIP',
      `Mes: ${monthLabel} — Total: ${inactiveTeachers.length} docente(s)`,
      ['Docente', 'Nivel', 'DNI'],
      rows,
      [90, 50, 40],
      `Docentes_Inactivos_${format(new Date(), 'yyyy-MM')}.pdf`
    );
  };

  // ✅ Descargar PDF: Docentes con Inasistencias este mes
  const handleDownloadNoShowPDF = () => {
    if (noShowTeachers.length === 0) {
      toast.info('No hay inasistencias registradas este mes para exportar.');
      return;
    }
    const monthLabel = format(new Date(), "MMMM yyyy", { locale: es });
    const rows = noShowTeachers.map(([name, count]) => [name, String(count)]);
    generateSimpleListPDF(
      'Docentes con Inasistencias',
      `Mes: ${monthLabel} — Total: ${noShowTeachers.length} docente(s), ${totalNoShows} inasistencia(s)`,
      ['Docente', 'Inasistencias'],
      rows,
      [130, 50],
      `Docentes_Inasistencias_${format(new Date(), 'yyyy-MM')}.pdf`
    );
  };

  // ✅ FUNCIÓN PARA RESERVAS POR DÍA (LUNES A VIERNES)
const reservationsCount = useMemo(() => {
  const counts = { mañana: {}, tarde: {} };
  reservations.forEach(res => {
    if (!res.date) return;
    // Usamos split para evitar el desfase horario 
    const dateKey = res.date.split('T')[0];
    const shift = (res.shift || res.turno || '').toLowerCase();

    if (shift.includes('mañana')) {
      counts.mañana[dateKey] = (counts.mañana[dateKey] || 0) + 1;
    } else if (shift.includes('tarde')) {
      counts.tarde[dateKey] = (counts.tarde[dateKey] || 0) + 1;
    }
  });
  return counts;
}, [reservations]);

  // ✅ Los bloques de horario ya NO están escritos a mano aquí: se piden al
  // backend (única fuente de verdad, en server.py). Así, si el horario
  // cambia, basta con editarlo en un solo lugar.
  const [timeSlotsByShift, setTimeSlotsByShift] = useState({ mañana: [], tarde: [] });

  useEffect(() => {
    axios.get(`${API}/system/time-slots`)
      .then((res) => setTimeSlotsByShift(res.data))
      .catch((err) => console.error('Error cargando los horarios:', err));
  }, []);

  // Número de hora (1H, 2H...) de un bloque, según su turno.
  const getHourNumber = (shift, timeSlot) => {
    const lista = timeSlotsByShift[shift] || [];
    const idx = lista.indexOf(timeSlot);
    return idx === -1 ? null : idx + 1;
  };

  // ✅ FUNCIÓN PARA TABLAS SEMANALES POR TURNO
  const getWeeklySchedule = (shift) => {
    const days = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    const dates = [];
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1);

    for (let i = 0; i < 6; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }

    const timeSlots = timeSlotsByShift[shift] || [];
    const shiftReservations = reservations.filter(
      r => (r.shift || r.turno)?.toLowerCase() === shift
    );

    const tableData = timeSlots.map(slot => {
      const row = { slot };
      days.forEach((day, idx) => {
        const date = dates[idx];
        const reservation = shiftReservations.find(r => {
          const resDateKey = r.date.split("T")[0]; // evita desfase horario
          const currentDateKey = format(date, "yyyy-MM-dd");

          return (
            resDateKey === currentDateKey &&
            r.time_slot === slot
          );
        });
        row[day] = reservation ? `${reservation.user_name || '—'}\n${reservation.nivel || ''}` : '-';
      });
      return row;
    });

    return { days, timeSlots, tableData };
  };

const [dniToReset, setDniToReset] = useState('');

const handleResetPassword = async () => {
  if (!dniToReset) {
    toast.error('Por favor, ingresa el DNI del usuario');
    return;
  }

  if (!window.confirm(`¿Estás seguro de resetear la clave del DNI ${dniToReset} a "123456"?`)) return;

  try {
    // Ajusta esta URL según tu API
    await axios.post(`${API}/admin/reset-password`, { 
      dni: dniToReset,
      new_password: '123456' 
    }, axiosConfig);
    
    toast.success('Contraseña restaurada con éxito a: 123456');
    setDniToReset('');
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Error al restaurar contraseña');
  }
};

const [newAdmin, setNewAdmin] = useState({ user: '', pass: '' });

const handleAddAdmin = async () => {
  if (!newAdmin.user || !newAdmin.pass) {
    toast.error("Completa ambos campos");
    return;
  }
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API}/admin/manage/add`, 
      { username: newAdmin.user, password: newAdmin.pass },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    toast.success(response.data.message);
    setNewAdmin({ user: '', pass: '' });
    fetchAdmins(); // <--- Esta línea ordena a la lista refrescarse sola
  } catch (error) {
    toast.error(error.response?.data?.detail || "Error al crear administrador");
  }
};

const [adminList, setAdminList] = useState([]);

// Función para traer los datos
const fetchAdmins = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API}/admin/manage/list`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    console.log("Admins cargados:", response.data); // Esto dirá en la consola si llegaron
    setAdminList(response.data);
  } catch (error) {
    console.error("Error:", error);
  }
};

const handleChangeAdminPass = async (username) => {
  const newPass = prompt(`Ingrese la nueva contraseña para ${username}:`);
  if (!newPass || newPass.length < 4) {
    toast.error("Contraseña demasiado corta o vacía");
    return;
  }

  try {
    const token = localStorage.getItem('token');
    await axios.put(`${API}/admin/manage/update-password`, 
      { username, password: newPass },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    toast.success("Contraseña actualizada correctamente");
  } catch (error) {
    toast.error("No se pudo actualizar la contraseña");
  }
};

// Forzar que cargue al entrar al panel
useEffect(() => {
  fetchAdmins();
}, []);

const handleDeleteAdmin = async (username) => {
  if (!window.confirm(`¿Estás seguro de eliminar el acceso al administrador ${username}?`)) return;

  try {
    const token = localStorage.getItem('token');
    await axios.delete(`${API}/admin/manage/delete/${username}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    toast.success("Administrador eliminado");
    fetchAdmins(); // Recargamos la lista
  } catch (error) {
    toast.error(error.response?.data?.detail || "Error al eliminar");
  }
};

const handleToggleUser = async (dni, currentStatus) => {
  try {
    const res = await axios.put(`${API}/admin/users/${dni}/toggle`, {}, axiosConfig);
    toast.success(res.data.message);
    loadUsers();
  } catch (error) {
    toast.error('Error al cambiar estado del usuario');
  }
};

 // Calculate dates for the week
  const weekDates = [];
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay() + 1);
  
  for (let i = 0; i < 6; i++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    weekDates.push(date);
  }
  
  const morningSchedule = getWeeklySchedule('mañana');
  const afternoonSchedule = getWeeklySchedule('tarde');
  const dates = weekDates;

const weekDateKeys = dates.map(date => format(date, 'yyyy-MM-dd'));

const morningWeekly = useMemo(() => {
  return Object.keys(reservationsCount.mañana).reduce((sum, key) => {
    return weekDateKeys.includes(key) ? sum + (reservationsCount.mañana[key] || 0) : sum;
  }, 0);
}, [reservationsCount.mañana, weekDateKeys]);

const afternoonWeekly = useMemo(() => {
  return Object.keys(reservationsCount.tarde).reduce((sum, key) => {
    return weekDateKeys.includes(key) ? sum + (reservationsCount.tarde[key] || 0) : sum;
  }, 0);
}, [reservationsCount.tarde, weekDateKeys]);

// ✅ Filtro por DNI/nombre del docente, por fecha exacta y por turno,
// aplicado antes de dividir en "actuales" y "pasadas"
const filteredReservationsForTab = useMemo(() => {
  let list = reservations;
  const query = reservationSearchQuery.trim().toLowerCase();
  if (query) {
    list = list.filter((r) =>
      (r.user_dni || '').toLowerCase().includes(query) ||
      (r.user_name || '').toLowerCase().includes(query)
    );
  }
  if (reservationSearchDate) {
    list = list.filter((r) => r.date === reservationSearchDate);
  }
  if (reservationSearchShift) {
    list = list.filter((r) => (r.shift || '').toLowerCase() === reservationSearchShift);
  }
  return list;
}, [reservations, reservationSearchQuery, reservationSearchDate, reservationSearchShift]);

const currentReservations = filteredReservationsForTab.filter(
  r => !isReservationPast(r)
);

const pastReservations = filteredReservationsForTab.filter(
  r => isReservationPast(r)
);
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a4d2e] via-[#2d6a4f] to-[#1a4d2e]">
      {/* Header */}
      <div className="bg-[#0f2419]/60 backdrop-blur-sm border-b border-[#f5e6d3]/20 p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img
              src="logo_aip.png"
              alt="AIP Logo"
              className="w-12 h-12 object-contain rounded-full"
            />
            <div>
              <h1 className="text-2xl font-bold text-[#f5e6d3]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Panel de Administración
              </h1>
              <p className="text-[#f5e6d3]/80 text-sm">Sistema de Reservas AIP</p>
            </div>
          </div>
          <Button 
          onClick={handleLogout} 
          variant="outline" 
          className="bg-white border-2 border-red-200 text-red-600 font-black hover:bg-red-600 hover:text-white hover:border-red-600 transition-all shadow-md px-6 py-2"
        >
          Cerrar Sesión
        </Button>
        </div>
      </div>

      {/* 📅 Recordatorio: reservas de HOY del turno ACTUAL (todas las de todos
          los docentes), bien visible apenas se abre el panel. Se puede apagar
          desde Configuración > Recordatorios (mismo interruptor que usan los docentes) */}
      {remindersEnabled && todaysPendingReservations.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <style>{`
            @keyframes ring-bell {
              0%, 100% { transform: rotate(0deg); }
              8% { transform: rotate(18deg); }
              16% { transform: rotate(-16deg); }
              24% { transform: rotate(14deg); }
              32% { transform: rotate(-12deg); }
              40% { transform: rotate(9deg); }
              48% { transform: rotate(-6deg); }
              56% { transform: rotate(3deg); }
              64%, 100% { transform: rotate(0deg); }
            }
            .reminder-ring {
              display: inline-block;
              transform-origin: 50% 15%;
              animation: ring-bell 2.4s ease-in-out infinite;
            }
          `}</style>
          <div className="rounded-2xl p-5 shadow-lg border-2 border-amber-300 bg-amber-50 flex items-start gap-4">
            <span className="text-3xl leading-none reminder-ring">⏰</span>
            <div>
              <p className="font-black text-amber-900">
                {todaysPendingReservations.length === 1
                  ? `Recuerda: hay 1 reserva pendiente hoy en el turno ${currentShift}`
                  : `Recuerda: hay ${todaysPendingReservations.length} reservas pendientes hoy en el turno ${currentShift}`}
              </p>
              <p className="text-amber-800 text-sm font-semibold mt-1">
                {todaysPendingReservations.map((r, i) => (
                  <span key={r.id}>
                    {getHourNumber(r.shift, r.time_slot) && `${getHourNumber(r.shift, r.time_slot)}H · `}
                    {r.time_slot} — {r.user_name || "Docente"}
                    {i < todaysPendingReservations.length - 1 ? " · " : ""}
                  </span>
                ))}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="users" className="w-full">
  <TabsList className="grid w-full grid-cols-5 bg-[#03361c] mb-8 rounded-2xl p-1.5 h-auto shadow-2xl border border-[#f5e6d3]/10">
    
    <TabsTrigger 
      value="users" 
      className="py-4 text-white font-bold text-base transition-all duration-200 
                 hover:bg-white/10 hover:text-[#f5e6d3]
                 data-[state=active]:bg-[#f5e6d3] data-[state=active]:text-[#0f2419] data-[state=active]:shadow-lg 
                 rounded-xl flex items-center justify-center gap-2"
    >
      👤 <span className="hidden md:inline">Usuarios</span>
    </TabsTrigger>

    <TabsTrigger 
      value="reservations" 
      className="py-4 text-white font-bold text-base transition-all duration-200 
                 hover:bg-white/10 hover:text-[#f5e6d3]
                 data-[state=active]:bg-[#f5e6d3] data-[state=active]:text-[#0f2419] data-[state=active]:shadow-lg 
                 rounded-xl flex items-center justify-center gap-2"
    >
      📅 <span className="hidden md:inline">Reservas</span>
    </TabsTrigger>

    <TabsTrigger 
      value="report" 
      className="py-4 text-white font-bold text-base transition-all duration-200 
                 hover:bg-white/10 hover:text-[#f5e6d3]
                 data-[state=active]:bg-[#f5e6d3] data-[state=active]:text-[#0f2419] data-[state=active]:shadow-lg 
                 rounded-xl flex items-center justify-center gap-2"
    >
      📊 <span className="hidden md:inline">Reportes</span>
    </TabsTrigger>

    <TabsTrigger 
      value="audit" 
      className="py-4 text-white font-bold text-base transition-all duration-200 
                 hover:bg-white/10 hover:text-[#f5e6d3]
                 data-[state=active]:bg-[#f5e6d3] data-[state=active]:text-[#0f2419] data-[state=active]:shadow-lg 
                 rounded-xl flex items-center justify-center gap-2"
    >
      🕵️ <span className="hidden md:inline">Auditoría</span>
    </TabsTrigger>

    <TabsTrigger 
      value="config" 
      className="py-4 text-white font-bold text-base transition-all duration-200 
                 hover:bg-white/10 hover:text-[#f5e6d3]
                 data-[state=active]:bg-[#f5e6d3] data-[state=active]:text-[#0f2419] data-[state=active]:shadow-lg 
                 rounded-xl flex items-center justify-center gap-2"
    >
      ⚙️ <span className="hidden md:inline">Configuración</span>
    </TabsTrigger>

  </TabsList>

          {/* PESTAÑA USUARIOS */}
          <TabsContent value="users">
            <div className="bg-[#f5e6d3]/95 rounded-2xl p-8 shadow-2xl">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">

                <h2 className="text-2xl sm:text-3xl font-bold text-[#1a4d2e] text-center sm:text-left">
                  Docentes Registrados ({users.length})
                </h2>

                <div className="w-full sm:w-auto">
                    <input
                      type="file"
                      id="excel-upload"
                      className="hidden"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                    />

                    <label
                      htmlFor="excel-upload"
                      className="
                        w-full sm:w-auto
                        flex items-center justify-center gap-2
                        bg-gradient-to-r from-green-700 to-green-800
                        text-white
                        px-6 py-3
                        rounded-lg
                        cursor-pointer
                        hover:shadow-xl
                        font-bold
                        transition
                      "
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <FileUp size={20} />
                          Cargar desde Excel
                        </>
                      )}
                    </label>
                    <p className="text-xs text-gray-500 mt-2 max-w-xs">
                      Columnas: DNI, Nombres, Apellidos, Email, Teléfono, <strong>Nivel</strong>.
                      Si el docente trabaja en varios niveles, sepáralos con coma en la
                      misma celda, ej: <span className="font-mono">Inicial,Primaria</span>.
                    </p>
                  </div>

                </div>      

              <form ref={userFormRef} onSubmit={handleCreateOrUpdateUser} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 bg-white rounded-2xl p-8 shadow-lg">
                <Input placeholder="DNI" value={formData.dni} onChange={(e) => setFormData({ ...formData, dni: e.target.value })} disabled={isEditing} required />
                <Input placeholder="Nombres" value={formData.nombres} onChange={(e) => setFormData({ ...formData, nombres: e.target.value })} required />
                <Input placeholder="Apellidos" value={formData.apellidos} onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })} required />
                <Input placeholder="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}/>
                <Input placeholder="Teléfono (opcional)" value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} />
                <div className="md:col-span-3">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">
                    Nivel(es) — puede seleccionar varios
                  </p>
                  <div className="flex flex-wrap gap-3 items-center">
                    {NIVEL_OPTIONS.map((opt) => {
                      const checked = parseNiveles(formData.nivel).includes(opt);
                      return (
                        <label
                          key={opt}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border cursor-pointer text-sm font-semibold transition-colors ${
                            checked
                              ? 'bg-[#1a4d2e] text-white border-[#1a4d2e]'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-[#1a4d2e]/50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleNivelInFormData(opt)}
                            className="hidden"
                          />
                          {opt}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="md:col-span-3 flex gap-4 justify-end">
                  <Button type="submit" disabled={loading} className="bg-[#1a4d2e] hover:bg-[#2d6a4f] text-white text-lg px-8 py-6">
                    {loading ? 'Guardando...' : isEditing ? 'Actualizar' : 'Registrar'}
                  </Button>
                  {isEditing && (
                    <Button type="button" onClick={resetForm} variant="outline" className="text-lg px-8 py-6">
                      Cancelar
                    </Button>
                  )}
                </div>
              </form>

              <div className="mb-4">
                <Input
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  placeholder="🔍 Buscar por DNI o nombre..."
                  className="w-full sm:w-96 bg-white"
                />
                {userSearchQuery.trim() && (
                  <p className="text-sm text-[#1a4d2e]/70 font-medium mt-1">
                    {sortedUsers.length} de {users.length} docente(s) encontrado(s)
                  </p>
                )}
              </div>

              <div className="overflow-x-auto bg-white rounded-xl shadow-md">
                <table className="w-full">
                  <thead className="bg-[#1a4d2e] text-[#f5e6d3]">
                    <tr>
                      <th className="p-4 text-left">DNI</th>
                      <th
                        className="p-4 text-left cursor-pointer select-none hover:bg-[#2d6a4f] transition-colors"
                        onClick={() => handleSortUsers('nombre')}
                      >
                        Nombre Completo
                        <span className="ml-1 inline-block w-3 text-xs">
                          {userSortField === 'nombre' ? (userSortDirection === 'asc' ? '▲' : '▼') : ''}
                        </span>
                      </th>
                      <th
                        className="p-4 text-left cursor-pointer select-none hover:bg-[#2d6a4f] transition-colors"
                        onClick={() => handleSortUsers('nivel')}
                      >
                        Nivel
                        <span className="ml-1 inline-block w-3 text-xs">
                          {userSortField === 'nivel' ? (userSortDirection === 'asc' ? '▲' : '▼') : ''}
                        </span>
                      </th>
                      <th className="p-4 text-center">Reservas (Histórico)</th>
                      <th className="p-4 text-center">Reservas (Este Mes)</th>
                      <th className="p-4 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-12 text-gray-500 text-lg">
                          {userSearchQuery.trim()
                            ? `No se encontraron docentes que coincidan con "${userSearchQuery.trim()}".`
                            : "No hay docentes registrados. Sube el Excel o registra manualmente."}
                        </td>
                      </tr>
                    ) : (
                      sortedUsers.map((u) => {
                        const stats = attendanceStatsByDni[u.dni] || { total: 0, asistio: 0, noAsistio: 0 };
                        const monthlyStats = monthlyAttendanceStatsByDni[u.dni] || { total: 0, asistio: 0, noAsistio: 0 };
                        const renderStatsBadge = (s) => (
                          s.total === 0 ? (
                            <span className="text-gray-400 text-sm">Sin reservas</span>
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              <span className="font-black text-[#1a4d2e] text-lg">{s.total}</span>
                              <div className="flex gap-1.5 text-[10px] font-bold">
                                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                  ✓ {s.asistio}
                                </span>
                                {s.noAsistio > 0 && (
                                  <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                    ✕ {s.noAsistio}
                                  </span>
                                )}
                              </div>
                            </div>
                          )
                        );
                        return (
                        <tr key={u.dni} className="border-b hover:bg-[#f5e6d3]/30">
                          <td className="p-4 font-mono">{u.dni}</td>
                          <td className="p-4">{u.nombres} {u.apellidos}</td>
                          <td className="p-4">
                            <div className="flex flex-wrap gap-1">
                              {parseNiveles(u.nivel).map((n) => (
                                <span key={n} className="bg-[#1a4d2e]/10 text-[#1a4d2e] text-[11px] font-bold px-2 py-0.5 rounded-full">
                                  {n}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="p-4 text-center">{renderStatsBadge(stats)}</td>
                          <td className="p-4 text-center">{renderStatsBadge(monthlyStats)}</td>
                          <td className="p-4 text-center space-x-3">
                            <Button size="sm" onClick={() => handleEdit(u)}>Editar</Button>
                            <button
                              onClick={() => handleToggleUser(u.dni, u.active)}
                              className={`px-3 py-1 rounded text-xs font-bold text-white transition-all ${
                                u.active !== false ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'
                              }`}
                            >
                              {u.active !== false ? '✅ Activo' : '🚫 Inactivo'}
                            </button>
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteUser(u.dni)}>Eliminar</Button>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* PESTAÑA RESERVAS - DISEÑO COMPLETO CON CUADROS POR DÍA */}
          <TabsContent value="reservations">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
             
              {/* Columna izquierda: Reservas Actuales */}
              <div className="bg-[#f5e6d3]/95 rounded-2xl p-6 sm:p-8 shadow-2xl">
  
                {/* Header */}
                <div className="flex flex-col gap-4 mb-8">
                  
                  {/* Título + contador */}
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1a4d2e] flex flex-wrap items-center gap-3">
                    {showPastReservations ? "Reservas Pasadas" : "Reservas Actuales"}

                    <span
                      key={showPastReservations ? "past" : "current"}
                      className="
                        bg-[#7e0505]
                        text-white
                        px-4 py-1
                        rounded-full
                        text-base sm:text-lg
                        min-w-[45px]
                        text-center
                        animate-pulse
                      "
                    >
                      {showPastReservations
                        ? pastReservations.length
                        : currentReservations.length}
                    </span>
                  </h2>

                  {/* Botón debajo del título */}
                  <button
                    onClick={() => setShowPastReservations(!showPastReservations)}
                    className={`
                      w-full
                      sm:w-fit
                      flex
                      items-center
                      justify-center
                      gap-2
                      px-5
                      py-2
                      rounded-xl
                      font-bold
                      transition
                      duration-300
                      ${
                        showPastReservations
                          ? "bg-[#1a4d2e] text-white hover:bg-[#163d25]"
                          : "bg-[#7e0505] text-white hover:bg-[#5f0404]"
                      }
                    `}
                  >
                    {showPastReservations ? (
                      <>
                        <CalendarDays size={18} />
                        Ver actuales
                      </>
                    ) : (
                      <>
                        <History size={18} />
                        Ver pasadas
                      </>
                    )}
                  </button>
                </div>

                {/* 🔍 Búsqueda: por DNI/nombre del docente, por fecha exacta y por turno */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <Input
                    value={reservationSearchQuery}
                    onChange={(e) => setReservationSearchQuery(e.target.value)}
                    placeholder="🔍 Buscar por DNI o nombre del docente..."
                    className="flex-1 bg-white"
                  />
                  <Input
                    type="date"
                    value={reservationSearchDate}
                    onChange={(e) => setReservationSearchDate(e.target.value)}
                    className="sm:w-52 bg-white"
                  />
                  <select
                    value={reservationSearchShift}
                    onChange={(e) => setReservationSearchShift(e.target.value)}
                    className="sm:w-44 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm font-medium text-gray-700"
                  >
                    <option value="">Todos los turnos</option>
                    <option value="mañana">Mañana</option>
                    <option value="tarde">Tarde</option>
                  </select>
                  {(reservationSearchQuery.trim() || reservationSearchDate || reservationSearchShift) && (
                    <Button
                      onClick={() => {
                        setReservationSearchQuery('');
                        setReservationSearchDate('');
                        setReservationSearchShift('');
                      }}
                      variant="outline"
                      className="border-gray-300 text-gray-600 hover:bg-gray-100 font-bold shrink-0"
                    >
                      Limpiar
                    </Button>
                  )}
                </div>
                
                {/* --- CAMBIO AQUÍ: Añadimos max-h, overflow-y-auto y pr-2 --- */}
                <div className="
                  space-y-6
                  max-h-[700px]
                  overflow-y-auto
                  pr-2
                  scrollbar-thin
                  scrollbar-thumb-gray-400
                  transition-all
                  duration-300
                ">
                    {(showPastReservations ? pastReservations : currentReservations).length === 0 ? (
                    <p className="text-center text-2xl text-[#7e0505]/60 py-16">
                      {(reservationSearchQuery.trim() || reservationSearchDate || reservationSearchShift)
                        ? "No se encontraron reservas con esos filtros"
                        : "No hay reservas"}
                    </p>
                  ) : (
                    (showPastReservations ? pastReservations : currentReservations)
                      .map((r) => {
                        const isPast = isReservationPast(r);
                        const noAsistio = r.asistio === false;
                        const siAsistio = r.asistio === true;
                        const pendiente = isPast && !noAsistio && !siAsistio;

                        return (
                          <div 
                            key={r.id || r._id}
                            className={`
                              bg-white
                              rounded-xl
                              p-6
                              shadow-lg
                              border
                              transform
                              transition-all
                              duration-300
                              hover:scale-[1.02]
                              ${
                                noAsistio
                                  ? 'border-solid border-red-300 bg-red-50/40'
                                  : pendiente
                                  ? 'border-solid border-amber-300 bg-amber-50/30'
                                  : isPast
                                  ? 'border-dashed border-gray-400 opacity-70'
                                  : 'border-solid border-[#1a4d2e]/10'
                              }
                              flex justify-between items-center
                            `}
                          >
                            <div className={noAsistio ? 'opacity-60' : ''}>
                              {noAsistio && (
                                <span className="inline-block bg-red-600 text-white text-[10px] font-black uppercase px-2 py-0.5 rounded mb-2 tracking-wide">
                                  No asistió
                                </span>
                              )}
                              {siAsistio && (
                                <span className="inline-block bg-green-600 text-white text-[10px] font-black uppercase px-2 py-0.5 rounded mb-2 tracking-wide">
                                  ✓ Asistió
                                </span>
                              )}
                              {pendiente && (
                                <span className="inline-block bg-amber-500 text-white text-[10px] font-black uppercase px-2 py-0.5 rounded mb-2 tracking-wide">
                                  ⏳ Pendiente de confirmar
                                </span>
                              )}
                              <p className={`font-bold text-xl text-[#990303] ${noAsistio ? 'line-through decoration-2' : ''}`}>
                                {r.user_name || 'Desconocido'}
                              </p>
                              <p className={`text-gray-600 mt-1 ${noAsistio ? 'line-through decoration-2' : ''}`}>
                                {r.date ? format(parse(r.date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy') : 'Fecha desconocida'} - {r.shift?.toUpperCase() || 'N/A'}
                              </p>
                              <p className={`font-semibold text-[#7e0505] mt-2 text-lg ${noAsistio ? 'line-through decoration-2' : ''}`}>
                                {getHourNumber(r.shift, r.time_slot) && (
                                  <span className="font-black">{getHourNumber(r.shift, r.time_slot)}H · </span>
                                )}
                                {r.time_slot}
                              </p>
                              {(r.grado || r.seccion || r.area || r.actividad) && (
                                <p className={`text-sm text-[#1a4d2e] mt-2 font-medium ${noAsistio ? 'line-through decoration-2' : ''}`}>
                                  {r.grado && `Grado: ${r.grado}`}{r.seccion && ` - Sección: ${r.seccion}`}
                                  {r.area && <><br />Área: {r.area}</>}
                                  {r.actividad && <><br />Actividad: {r.actividad}</>}
                                </p>
                              )}
                              {r.attachments && r.attachments.length > 0 && (
                                <p className="text-xs text-blue-600 font-bold mt-2 truncate max-w-xs">
                                  📎 {r.attachments.length === 1 ? r.attachments[0].filename : `${r.attachments.length} archivos adjuntos`}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 items-end">
                              {r.attachments && r.attachments.length > 0 && (
                                <div className="flex flex-col gap-1 items-end">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadReservationAttachments(r)}
                                    disabled={downloadingAttachmentId === (r.id || r._id)}
                                    className="border-blue-500 text-blue-600 hover:bg-blue-600 hover:text-white font-bold disabled:opacity-60 max-w-[240px] truncate"
                                    title={r.attachments.map((a) => a.filename).join('\n')}
                                  >
                                    {downloadingAttachmentId === (r.id || r._id)
                                      ? '⏳ Preparando carpeta...'
                                      : `📁 Descargar adjuntos (${r.attachments.length}) en carpeta`}
                                  </Button>
                                </div>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDetailReservation(r)}
                                className="border-[#1a4d2e] text-[#1a4d2e] hover:bg-[#1a4d2e] hover:text-white font-bold"
                              >
                                📋 Detalles
                              </Button>
                              {isPast && pendiente && (
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleSetAttendance(r, true)}
                                    className="border-green-600 text-green-700 hover:bg-green-600 hover:text-white font-bold"
                                  >
                                    ✓ Asistió
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleSetAttendance(r, false)}
                                    className="border-red-500 text-red-600 hover:bg-red-500 hover:text-white font-bold"
                                  >
                                    ✕ No asistió
                                  </Button>
                                </div>
                              )}
                              {isPast && !pendiente && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleAttendance(r)}
                                  className={
                                    noAsistio
                                      ? 'border-green-600 text-green-700 hover:bg-green-600 hover:text-white font-bold'
                                      : 'border-red-500 text-red-600 hover:bg-red-500 hover:text-white font-bold'
                                  }
                                >
                                  {noAsistio ? '✓ Marcar asistió' : '✕ Marcar no asistió'}
                                </Button>
                              )}
                              <Button 
                                variant="destructive" 
                                size="lg" 
                                onClick={() => handleDeleteReservation(r.id || r._id)}
                              >
                                Eliminar
                              </Button>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
              </div>

              {/* Columna derecha: Cuadros por día + Turnos */}
              <div className="bg-[#f5e6d3]/95 rounded-2xl p-6 shadow-2xl">
                <h2 className="text-xl font-bold text-[#1a4d2e] mb-4 text-center">Reservas por Semana Actual y Turno</h2>
                
                {/* --- BLOQUE DE CUADROS POR DÍA Y TURNO --- */}
               <div className="flex flex-col gap-8 mb-10">
                
                {/* PANEL MAÑANA */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 border-t-8 border-t-blue-600 w-full">
                  <h3 className="text-blue-700 font-bold mb-4 flex items-center gap-2 text-xl">☀️ RESERVAS TURNO: MAÑANA</h3>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
                    {dates.map((date, idx) => {
                      const dKey = format(date, 'yyyy-MM-dd');
                      const count = reservationsCount.mañana[dKey] || 0;
                      return (
                        <div key={idx} className="text-center">
                          <p className="text-xs text-gray-400 font-bold uppercase mb-2">
                            {format(date, 'eee dd', { locale: es })}
                          </p>
                          <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center text-xl font-black border-2 transition-all ${
                            count > 0 ? 'bg-blue-600 text-white border-blue-700 shadow-md scale-105' : 'bg-gray-50 text-gray-200 border-gray-100'
                          }`}>
                            {count}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* PANEL TARDE */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 border-t-8 border-t-orange-600 w-full">
                  <h3 className="text-orange-700 font-bold mb-4 flex items-center gap-2 text-xl">⛅ RESERVAS TURNO: TARDE</h3>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
                    {dates.map((date, idx) => {
                      const dKey = format(date, 'yyyy-MM-dd');
                      const count = reservationsCount.tarde[dKey] || 0;
                      return (
                        <div key={idx} className="text-center">
                          <p className="text-xs text-gray-400 font-bold uppercase mb-2">
                            {format(date, 'eee dd', { locale: es })}
                          </p>
                          <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center text-xl font-black border-2 transition-all ${
                            count > 0 ? 'bg-orange-600 text-white border-orange-700 shadow-md scale-105' : 'bg-gray-50 text-gray-200 border-gray-100'
                          }`}>
                            {count}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
                  
              <h2 className="text-xl font-bold text-[#1a4d2e] mb-4 text-center">Reservas por Turno (Semana Actual)</h2>
                
                {/* Turno Mañana */}
                <div className="bg-white rounded-xl p-4 mb-4 border-l-4 border-blue-600 shadow">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold text-blue-800">Turno Mañana</h3>
                   <span className="text-2xl font-bold text-blue-800">{morningWeekly}</span>
                  </div>
                  <p className="text-blue-600 text-xs">7:30 - 12:30</p>
                </div>

                {/* Turno Tarde */}
                <div className="bg-white rounded-xl p-4 mb-4 border-l-4 border-orange-600 shadow">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold text-orange-800">Turno Tarde</h3>
                    <span className="text-2xl font-bold text-orange-800">{afternoonWeekly}</span>
                  </div>
                  <p className="text-orange-600 text-xs">13:00 - 18:20</p>
                </div>
              </div>
            </div>

           {/* ✅ DISTRIBUCIÓN SEMANAL - TURNO MAÑANA (FONDO BLANCO + DATOS EXCLUSIVOS) */}
            <div className="mt-8 bg-white rounded-2xl p-8 shadow-2xl border border-[#1a4d2e]/20">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-[#1a4d2e]">Distribución Semanal (Semana Actual) - Turno Mañana</h2>
                <Button 
                  onClick={() => generateAdminTablePDF(
                    morningSchedule.tableData, 
                    morningSchedule.timeSlots, 
                    morningSchedule.days, 
                    'Mañana', 
                    dates
                  )}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Descargar PDF Cuadro
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                  <thead className="bg-blue-700 text-white">
                    <tr>
                      <th className="p-4 text-center">Horario</th>
                      {morningSchedule.days.map((day, idx) => (
                        <th key={day} className="p-4 text-center min-w-[120px]">
                          <div className="font-bold">{day.charAt(0).toUpperCase() + day.slice(1)}</div>
                          <div className="text-xs mt-1 bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                            {format(dates[idx], 'dd MMM', { locale: es })}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {morningSchedule.timeSlots.map((slot, idx) => (
                      <tr key={idx} className="border-b border-gray-200 hover:bg-blue-50/30">
                        <td className="p-4 font-bold text-[#1a4d2e] bg-blue-50/50 min-w-[150px]">{slot}</td>
                        {morningSchedule.days.map(day => (
                          <td key={`${idx}-${day}`} className="p-4 text-center align-top">
                            {morningSchedule.tableData[idx][day] !== '-' ? (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-2 font-bold text-[#1a4d2e]">
                                {morningSchedule.tableData[idx][day]}
                              </div>
                            ) : (
                              <span className="text-gray-300 font-medium">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ✅ DISTRIBUCIÓN SEMANAL - TURNO TARDE (FONDO BLANCO + DATOS EXCLUSIVOS) */}
            <div className="mt-8 bg-white rounded-2xl p-8 shadow-2xl border border-[#1a4d2e]/20">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-[#1a4d2e]">Distribución Semanal (Semana Actual) - Turno Tarde</h2>
                <Button 
                  onClick={() => generateAdminTablePDF(
                    afternoonSchedule.tableData, 
                    afternoonSchedule.timeSlots, 
                    afternoonSchedule.days, 
                    'Tarde', 
                    dates
                  )}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  Descargar PDF Cuadro
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                  <thead className="bg-orange-700 text-white">
                    <tr>
                      <th className="p-4 text-center">Horario</th>
                      {afternoonSchedule.days.map((day, idx) => (
                        <th key={day} className="p-4 text-center min-w-[120px]">
                          <div className="font-bold">{day.charAt(0).toUpperCase() + day.slice(1)}</div>
                          <div className="text-xs mt-1 bg-orange-100 text-orange-800 px-2 py-0.5 rounded">
                            {format(dates[idx], 'dd MMM', { locale: es })}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {afternoonSchedule.timeSlots.map((slot, idx) => (
                      <tr key={idx} className="border-b border-gray-200 hover:bg-orange-50/30">
                        <td className="p-4 font-bold text-[#1a4d2e] bg-orange-50/50 min-w-[150px]">{slot}</td>
                        {afternoonSchedule.days.map(day => (
                          <td key={`${idx}-${day}`} className="p-4 text-center align-top">
                            {afternoonSchedule.tableData[idx][day] !== '-' ? (
                              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 font-bold text-[#1a4d2e]">
                                {afternoonSchedule.tableData[idx][day]}
                              </div>
                            ) : (
                              <span className="text-gray-300 font-medium">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* PESTAÑA REPORTES */}
            <TabsContent value="report">
              <div ref={reportSnapshotRef} className="bg-[#f5e6d3]/95 rounded-2xl p-8 shadow-2xl animate-in fade-in duration-500">
                
                {/* Encabezado del Reporte */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-10 border-b border-[#1a4d2e]/10 pb-6">
                  <div>
                    <h2 className="text-3xl font-black text-[#1a4d2e]">
                      📊 Reporte Mensual
                    </h2>
                    <p className="text-lg text-[#1a4d2e]/70 font-medium mt-1">
                      {format(currentDate, 'MMMM yyyy', { locale: es }).toUpperCase()}
                    </p>
                  </div>
                  <div data-html2canvas-ignore="true" className="flex flex-col sm:flex-row gap-3 mt-4 md:mt-0">
                    <Button
                      onClick={handleDownloadReport}
                      className="bg-[#1a4d2e] hover:bg-[#2d6a4f] text-white text-lg px-8 py-6 font-bold rounded-xl shadow-lg flex items-center gap-3 transition-transform hover:scale-105"
                    >
                      📄 Descargar Informe del PIP
                    </Button>
                    <Button
                      onClick={handleDownloadReportSnapshot}
                      disabled={isGeneratingSnapshot}
                      className="bg-[#7a4f1a] hover:bg-[#9a6a2a] text-white text-lg px-8 py-6 font-bold rounded-xl shadow-lg flex items-center gap-3 transition-transform hover:scale-105 disabled:opacity-60"
                    >
                      {isGeneratingSnapshot ? '⏳ Generando...' : '📸 Descargar Pestaña en PDF'}
                    </Button>
                    <Button
                      onClick={handleExportExcel}
                      disabled={isExportingExcel}
                      className="bg-emerald-700 hover:bg-emerald-600 text-white text-lg px-8 py-6 font-bold rounded-xl shadow-lg flex items-center gap-3 transition-transform hover:scale-105 disabled:opacity-60"
                    >
                      {isExportingExcel ? '⏳ Generando...' : '📈 Descargar Excel (todo)'}
                    </Button>
                  </div>
                </div>

                {/* 1. TARJETAS DE RESUMEN (KPIs) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                  {/* Total */}
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-8 border-[#1a4d2e] flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 font-bold text-xs uppercase tracking-wider">Total Reservas</p>
                      <p className="text-5xl font-black text-[#1a4d2e] mt-2">{totalMonthly}</p>
                    </div>
                    <div className="bg-[#1a4d2e]/10 p-4 rounded-full text-4xl">📈</div>
                  </div>
                  
                  {/* Mañana */}
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-8 border-blue-600 flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 font-bold text-xs uppercase tracking-wider">Turno Mañana</p>
                      <p className="text-5xl font-black text-blue-700 mt-2">{morningMonthly}</p>
                      <p className="text-xs text-blue-600 font-bold mt-1">
                        {totalMonthly > 0 ? ((morningMonthly / totalMonthly) * 100).toFixed(0) : 0}% del total
                      </p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-full text-4xl">☀️</div>
                  </div>

                  {/* Tarde */}
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-8 border-orange-600 flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 font-bold text-xs uppercase tracking-wider">Turno Tarde</p>
                      <p className="text-5xl font-black text-orange-700 mt-2">{afternoonMonthly}</p>
                      <p className="text-xs text-orange-600 font-bold mt-1">
                        {totalMonthly > 0 ? ((afternoonMonthly / totalMonthly) * 100).toFixed(0) : 0}% del total
                      </p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-full text-4xl">⛅</div>
                  </div>
                </div>

                {/* 2. NIVEL EDUCATIVO Y TOP DOCENTES */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
                  
                  {/* Gráfico de Barras: Niveles */}
                  <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                    <h3 className="text-xl font-bold text-[#1a4d2e] mb-6 flex items-center gap-2">
                      🏫 Distribución por Nivel
                    </h3>
                    <div className="space-y-6">
                      {levels.map(level => {
                        const count = reservationsByLevel[level] || 0;
                        const percentage = totalMonthly > 0 ? (count / totalMonthly) * 100 : 0;
                        // Color dinámico según nivel
                        let colorClass = "bg-gray-500";
                        if (level === 'Inicial') colorClass = "bg-pink-500";
                        if (level === 'Primaria') colorClass = "bg-yellow-500";
                        if (level === 'Secundaria') colorClass = "bg-purple-600";

                        return (
                          <div key={level}>
                            <div className="flex justify-between text-sm font-bold text-gray-700 mb-1">
                              <span>{level}</span>
                              <span>{count} reservas ({percentage.toFixed(0)}%)</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${colorClass} transition-all duration-1000 ease-out`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-8 p-4 bg-green-50 rounded-xl border border-green-100 text-center">
                      <p className="text-sm text-green-800 font-medium">Nivel Líder</p>
                      <p className="text-2xl font-black text-green-700">{maxLevel}</p>
                    </div>

                    {/* 3. ALERTA DE INACTIVOS (Mejorada visualmente) */}
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-red-100 mt-8">
                      <div className="bg-red-50 p-6 border-b border-red-100 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                        <div>
                          <h3 className="text-xl font-bold text-red-800 flex items-center gap-2">
                            💤 Docentes que NO ingresarón al AIP este mes:
                          </h3>
                          <p className="text-sm text-red-600 mt-1">
                            Se recomienda incentivar el uso del AIP a estos {inactiveTeachers.length} docentes.
                          </p>
                        </div>
                        {inactiveTeachers.length > 0 && (
                          <Button
                            onClick={handleDownloadInactivePDF}
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 text-white font-bold shrink-0"
                          >
                            📄 Descargar PDF
                          </Button>
                        )}
                      </div>
                      
                      <div className="max-h-[300px] overflow-y-auto p-0">
                        {inactiveTeachers.length === 0 ? (
                          <div className="p-8 text-center">
                            <p className="text-4xl mb-2">🎉</p>
                            <p className="text-green-700 font-bold text-lg">¡Excelente! Todos los docentes han usado el sistema.</p>
                          </div>
                        ) : (
                          <table className="w-full text-left">
                            <thead className="bg-gray-50 text-gray-500 text-xs uppercase sticky top-0 z-10 shadow-sm">
                              <tr>
                                <th className="p-4 font-bold">Docente</th>
                                <th className="p-4 font-bold">Nivel</th>
                                <th className="p-4 font-bold text-right">DNI</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {inactiveTeachers.map((user, idx) => (
                                <tr key={user.dni} className="hover:bg-red-50/20 transition-colors">
                                  <td className="p-4 font-medium text-gray-700 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-red-400"></span>
                                    {user.nombres} {user.apellidos}
                                  </td>
                                  <td className="p-4 text-gray-500 text-sm">
                                    <div className="flex flex-wrap gap-1">
                                      {parseNiveles(user.nivel).map((n) => (
                                        <span key={n} className="bg-gray-100 px-2 py-1 rounded text-xs font-bold">{n}</span>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="p-4 text-right font-mono text-gray-400 text-sm">{user.dni}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>

                    {/* 4. DOCENTES CON INASISTENCIAS (reservaron pero no asistieron) */}
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-orange-100 mt-8">
                      <div className="bg-orange-50 p-6 border-b border-orange-100 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                        <div>
                          <h3 className="text-xl font-bold text-orange-800 flex items-center gap-2">
                            🚫 Docentes con Inasistencias este mes
                          </h3>
                          <p className="text-sm text-orange-600 mt-1">
                            {totalNoShows === 0
                              ? 'Ningún docente ha faltado a una reserva confirmada este mes.'
                              : `${noShowTeachers.length} docente(s) con un total de ${totalNoShows} inasistencia(s) registrada(s).`}
                          </p>
                        </div>
                        {noShowTeachers.length > 0 && (
                          <Button
                            onClick={handleDownloadNoShowPDF}
                            size="sm"
                            className="bg-orange-600 hover:bg-orange-700 text-white font-bold shrink-0"
                          >
                            📄 Descargar PDF
                          </Button>
                        )}
                      </div>

                      <div className="max-h-[300px] overflow-y-auto p-0">
                        {noShowTeachers.length === 0 ? (
                          <div className="p-8 text-center">
                            <p className="text-4xl mb-2">✅</p>
                            <p className="text-green-700 font-bold text-lg">Sin inasistencias registradas este mes.</p>
                          </div>
                        ) : (
                          <table className="w-full text-left">
                            <thead className="bg-gray-50 text-gray-500 text-xs uppercase sticky top-0 z-10 shadow-sm">
                              <tr>
                                <th className="p-4 font-bold">Docente</th>
                                <th className="p-4 font-bold text-right">Veces que no asistió</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {noShowTeachers.map(([name, count], idx) => (
                                <tr key={idx} className="hover:bg-orange-50/30 transition-colors">
                                  <td className="p-4 font-medium text-gray-700 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                                    {name}
                                  </td>
                                  <td className="p-4 text-right">
                                    <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-black">
                                      {count}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Top 5 Docentes, por nivel — un solo contenedor para que ocupe
                      correctamente la columna derecha del grid de 2 columnas */}
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-bold text-[#1a4d2e]">🏆 Top 5 Docentes Activos</h3>
                      <Button
                        onClick={handleDownloadTopTeachersPDF}
                        size="sm"
                        className="bg-[#1a4d2e] hover:bg-[#0f2419] text-white font-bold"
                      >
                        📄 Descargar PDF
                      </Button>
                    </div>
                    <div className="space-y-8">
                      {levels.map(level => {
                        const topForLevel = topTeachersByLevel[level];
                        let badgeColor = "bg-gray-500";
                        let headerGradient = "from-gray-500 to-gray-600";
                        let emoji = "🏆";
                        if (level === 'Inicial') { badgeColor = "bg-pink-500"; headerGradient = "from-pink-500 to-pink-600"; emoji = "🎈"; }
                        if (level === 'Primaria') { badgeColor = "bg-yellow-500"; headerGradient = "from-yellow-500 to-amber-600"; emoji = "✏️"; }
                        if (level === 'Secundaria') { badgeColor = "bg-blue-600"; headerGradient = "from-blue-600 to-blue-700"; emoji = "🎓"; }

                        return (
                          <div key={level} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                            <div className={`bg-gradient-to-r ${headerGradient} px-8 py-5`}>
                              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                {emoji} Top 5 Docentes Activos - {level}
                              </h3>
                            </div>
                            <div className="p-8">
                            {topForLevel.length === 0 ? (
                              <p className="text-center text-gray-400 py-10">No hay datos suficientes aún.</p>
                            ) : (
                              <div className="space-y-4">
                                {topForLevel.map(([name, count], index) => (
                                  <div key={index} className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                                    <div className={`
                                      w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-sm
                                      ${index === 0 ? `${badgeColor} text-xl` :
                                        index === 1 ? 'bg-gray-400' :
                                        index === 2 ? 'bg-orange-400' : 'bg-[#1a4d2e]/80'}
                                    `}>
                                      {index + 1}
                                    </div>
                                    <div className="flex-1">
                                      <p className="font-bold text-gray-800">{name}</p>
                                      <div className="w-full bg-gray-200 h-1.5 rounded-full mt-1.5">
                                        <div className={`${badgeColor} h-1.5 rounded-full`} style={{ width: `${(count / topForLevel[0][1]) * 100}%` }}></div>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <span className="block text-2xl font-black text-[#1a4d2e]">{count}</span>
                                      <span className="text-[10px] text-gray-500 uppercase font-bold">Reservas</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

              </div>

              {/* ✅ TENDENCIA ANUAL: reservas por mes, año actual vs año anterior */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mt-10 p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-[#1a4d2e] flex items-center gap-2">
                      📈 Tendencia Anual de Reservas
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {annualTrends
                        ? `${annualTrends.year} (barra oscura) vs ${annualTrends.previous_year} (barra clara), mes a mes.`
                        : 'Cargando...'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={annualTrendsYear}
                      onChange={(e) => {
                        const y = Number(e.target.value);
                        setAnnualTrendsYear(y);
                        loadAnnualTrends(y);
                      }}
                      className="border-2 border-gray-200 rounded-xl px-3 py-2 font-bold text-sm"
                    >
                      {[0, 1, 2, 3].map((offset) => {
                        const y = new Date().getFullYear() - offset;
                        return <option key={y} value={y}>{y}</option>;
                      })}
                    </select>
                    {annualTrends && (
                      <Button
                        onClick={handleDownloadAnnualTrendsPDF}
                        size="sm"
                        className="bg-[#1a4d2e] hover:bg-[#0f2419] text-white font-bold"
                      >
                        📄 Descargar PDF
                      </Button>
                    )}
                  </div>
                </div>

                {annualTrends && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border-2 border-emerald-200">
                        <p className="text-sm text-emerald-700 font-medium">Total {annualTrends.year}</p>
                        <p className="text-3xl font-black text-emerald-800">{annualTrends.total_current}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                        <p className="text-sm text-gray-500 font-medium">Total {annualTrends.previous_year}</p>
                        <p className="text-3xl font-black text-gray-600">{annualTrends.total_previous}</p>
                      </div>
                    </div>

                    <div className="flex items-end justify-between gap-1 sm:gap-2 h-48 border-b-2 border-gray-100 pb-1">
                      {annualTrends.months.map((m) => {
                        const maxVal = Math.max(
                          1,
                          ...annualTrends.months.map((x) => Math.max(x.current, x.previous))
                        );
                        const currentH = Math.round((m.current / maxVal) * 100);
                        const prevH = Math.round((m.previous / maxVal) * 100);
                        return (
                          <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full">
                            <div className="flex items-end gap-0.5 h-full w-full justify-center">
                              <div
                                className="w-1/2 max-w-[14px] bg-gray-200 rounded-t"
                                style={{ height: `${prevH}%` }}
                                title={`${annualTrends.previous_year}: ${m.previous}`}
                              />
                              <div
                                className="w-1/2 max-w-[14px] bg-[#1a4d2e] rounded-t"
                                style={{ height: `${currentH}%` }}
                                title={`${annualTrends.year}: ${m.current}`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between gap-1 sm:gap-2 mt-2">
                      {annualTrends.months.map((m) => (
                        <span key={m.month} className="flex-1 text-center text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase">
                          {m.label.slice(0, 3)}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* ✅ REPORTES MENSUALES HISTÓRICOS (generados automáticamente por el servidor) */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mt-10">
                <div className="bg-[#1a4d2e]/5 p-6 border-b border-[#1a4d2e]/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-[#1a4d2e] flex items-center gap-2">
                      📂 Reportes Mensuales Históricos
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      El sistema genera automáticamente un reporte el último día de cada mes. Aquí puedes ver y descargar los de meses anteriores.
                    </p>
                  </div>
                  <Button
                    onClick={handleGenerateReportNow}
                    disabled={generatingReportNow}
                    variant="outline"
                    className="border-[#1a4d2e] text-[#1a4d2e] hover:bg-[#1a4d2e] hover:text-white font-bold disabled:opacity-60 whitespace-nowrap"
                  >
                    {generatingReportNow ? '⏳ Generando...' : '🔄 Generar reporte de este mes ahora'}
                  </Button>
                </div>

                <div className="p-6">
                  {loadingMonthlyReports ? (
                    <p className="text-center text-gray-400 py-8">Cargando reportes...</p>
                  ) : monthlyReports.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">
                      Todavía no hay reportes mensuales generados. El primero se creará automáticamente al final de este mes,
                      o puedes generar uno ahora con el botón de arriba.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {monthlyReports.map((report) => (
                        <div
                          key={report.id}
                          className="flex items-center justify-between gap-3 p-4 rounded-xl border border-gray-100 hover:border-[#1a4d2e]/30 hover:shadow-md transition-all"
                        >
                          <div>
                            <p className="font-bold text-[#1a4d2e]">
                              {MESES_NOMBRES[report.month - 1]} {report.year}
                            </p>
                            {report.created_at && (
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                Generado: {format(new Date(report.created_at), 'dd/MM/yyyy HH:mm')}
                              </p>
                            )}
                          </div>
                          <Button
                            onClick={() => handleDownloadMonthlyReport(report)}
                            size="sm"
                            className="bg-[#1a4d2e] hover:bg-[#2d6a4f] text-white font-bold shrink-0"
                          >
                            ⬇ PDF
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

          {/* PESTAÑA AUDITORÍA */}
          <TabsContent value="audit">
            <div className="bg-[#f5e6d3]/95 rounded-2xl p-8 shadow-2xl">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-black text-[#1a4d2e] flex items-center gap-2">
                    🕵️ Bitácora de Auditoría
                  </h2>
                  <p className="text-[#1a4d2e]/70 font-medium mt-1">
                    Registro de acciones importantes hechas por administradores: quién, qué y cuándo.
                  </p>
                </div>
                <Button
                  onClick={loadAuditLogs}
                  variant="outline"
                  className="border-[#1a4d2e] text-[#1a4d2e] hover:bg-[#1a4d2e] hover:text-white font-bold"
                >
                  🔄 Actualizar
                </Button>
              </div>

              {/* Filtros: por administrador y por rango de fechas */}
              <div className="bg-white rounded-2xl shadow-lg p-5 mb-6 border border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                  <div>
                    <label className="block text-xs font-bold text-[#1a4d2e] uppercase mb-1">
                      Administrador
                    </label>
                    <Input
                      value={auditFilterAdmin}
                      onChange={(e) => setAuditFilterAdmin(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') loadAuditLogs(); }}
                      placeholder="Buscar por nombre..."
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#1a4d2e] uppercase mb-1">
                      Desde
                    </label>
                    <Input
                      type="date"
                      value={auditFilterDateFrom}
                      onChange={(e) => setAuditFilterDateFrom(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#1a4d2e] uppercase mb-1">
                      Hasta
                    </label>
                    <Input
                      type="date"
                      value={auditFilterDateTo}
                      onChange={(e) => setAuditFilterDateTo(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={loadAuditLogs}
                      className="bg-[#1a4d2e] hover:bg-[#0f2419] text-white font-bold flex-1"
                    >
                      Filtrar
                    </Button>
                    <Button
                      onClick={() => {
                        setAuditFilterAdmin('');
                        setAuditFilterDateFrom('');
                        setAuditFilterDateTo('');
                        // Limpiamos el ref directamente para no depender del
                        // timing del useEffect antes de recargar.
                        auditFiltersRef.current = { admin: '', dateFrom: '', dateTo: '' };
                        loadAuditLogs();
                      }}
                      variant="outline"
                      className="border-gray-300 text-gray-600 hover:bg-gray-100 font-bold"
                    >
                      Limpiar
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                {loadingAuditLogs ? (
                  <p className="text-center text-gray-400 py-12">Cargando...</p>
                ) : auditLogsError ? (
                  <p className="text-center text-red-600 font-bold py-12">
                    ⚠️ {auditLogsError}
                  </p>
                ) : auditLogs.length === 0 ? (
                  <p className="text-center text-gray-400 py-12">
                    No se encontraron acciones con los filtros aplicados.
                  </p>
                ) : (
                  <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table className="w-full text-left">
                      <thead className="bg-[#1a4d2e] text-white text-xs uppercase sticky top-0 z-10">
                        <tr>
                          <th className="p-4 font-bold">Fecha</th>
                          <th className="p-4 font-bold">Administrador</th>
                          <th className="p-4 font-bold">Acción</th>
                          <th className="p-4 font-bold">Detalle</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {auditLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-[#f5e6d3]/20 transition-colors">
                            <td className="p-4 text-sm text-gray-500 whitespace-nowrap">
                              {log.created_at ? format(new Date(log.created_at), 'dd/MM/yyyy HH:mm') : '—'}
                            </td>
                            <td className="p-4 font-bold text-[#1a4d2e] whitespace-nowrap">
                              {log.admin_username}
                            </td>
                            <td className="p-4">
                              <span className="bg-[#1a4d2e]/10 text-[#1a4d2e] text-xs font-bold px-2 py-1 rounded-full">
                                {log.action}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-gray-600">{log.details || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* PESTAÑA CONFIGURACIÓN - MEJORADA */}
          <TabsContent value="config">
            <div className="bg-[#f5e6d3]/95 rounded-2xl p-8 shadow-2xl space-y-8">
              <h2 className="text-3xl font-bold text-[#1a4d2e] mb-4">Configuración del Sistema</h2>

              {/* Switch de Visibilidad del Top 5 para docentes */}
              <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-gray-100">
                <h3 className="text-xl font-bold text-[#1a4d2e] mb-2">Top 5 Docentes Activos (vista de docentes)</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Controla si los docentes, directivos y AIP pueden ver el ranking de Top 5
                  en su propio panel. Directivos y AIP siempre ven los tres niveles
                  (Inicial, Primaria, Secundaria); los docentes ven el o los suyos.
                </p>
                <div className="flex items-center justify-center gap-6">
                  <p className="text-lg font-medium">
                    Visible para docentes: <span className={topTeachersVisible ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                      {topTeachersVisible ? "SÍ" : "NO"}
                    </span>
                  </p>
                  <button
                    onClick={handleToggleTopTeachers}
                    className={`relative inline-flex h-10 w-20 items-center rounded-full transition-colors focus:outline-none ${topTeachersVisible ? 'bg-green-600' : 'bg-red-500'}`}
                  >
                    <span className={`inline-block h-8 w-8 transform rounded-full bg-white transition-transform ${topTeachersVisible ? 'translate-x-11' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>

              {/* Switch de Recordatorios (banner en la app + notificaciones push) */}
              <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-gray-100">
                <h3 className="text-xl font-bold text-[#1a4d2e] mb-2">Recordatorios de Reservas</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Controla el aviso "Recuerda: tienes una reserva hoy" que ven los docentes al
                  entrar, y las notificaciones push que reciben 1 hora antes de su reserva
                  (aunque no tengan la app abierta).
                </p>
                <div className="flex items-center justify-center gap-6">
                  <p className="text-lg font-medium">
                    Recordatorios activos: <span className={remindersEnabled ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                      {remindersEnabled ? "SÍ" : "NO"}
                    </span>
                  </p>
                  <button
                    onClick={handleToggleReminders}
                    disabled={remindersToggleLoading}
                    className={`relative inline-flex h-10 w-20 items-center rounded-full transition-colors focus:outline-none ${remindersEnabled ? 'bg-green-600' : 'bg-red-500'}`}
                  >
                    <span className={`inline-block h-8 w-8 transform rounded-full bg-white transition-transform ${remindersEnabled ? 'translate-x-11' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>

              {/* Modo de Asistencia: Manual vs Check-in QR */}
              <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-gray-100">
                <h3 className="text-xl font-bold text-[#1a4d2e] mb-2">Modo de Registro de Asistencia</h3>
                <p className="text-sm text-gray-500 mb-4">
                  <strong>Manual:</strong> tú marcas "asistió / no asistió" desde la pestaña Reservas.{' '}
                  <strong>Check-in QR:</strong> el docente marca su propia asistencia al escanear el
                  código impreso en la sala del AIP. En cualquiera de los dos modos, tú siempre puedes
                  corregir la asistencia manualmente si hace falta.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => handleSetAttendanceMode('manual')}
                    disabled={attendanceModeLoading}
                    className={`px-5 py-3 rounded-xl font-bold border-2 transition-colors ${
                      attendanceMode === 'manual'
                        ? 'bg-[#1a4d2e] text-white border-[#1a4d2e]'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#1a4d2e]/50'
                    }`}
                  >
                    ✍️ Manual
                  </button>
                  <button
                    onClick={() => handleSetAttendanceMode('qr')}
                    disabled={attendanceModeLoading}
                    className={`px-5 py-3 rounded-xl font-bold border-2 transition-colors ${
                      attendanceMode === 'qr'
                        ? 'bg-[#1a4d2e] text-white border-[#1a4d2e]'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#1a4d2e]/50'
                    }`}
                  >
                    📷 Check-in QR
                  </button>
                </div>

                {attendanceMode === 'qr' && (() => {
                  if (!checkinSecret) {
                    return (
                      <div className="mt-6 pt-6 border-t border-gray-100 text-sm text-gray-500">
                        Generando código de seguridad del QR...
                      </div>
                    );
                  }
                  const checkinUrl = `${window.location.origin}/user-dashboard?checkin=1&code=${encodeURIComponent(checkinSecret)}`;
                  const qrImgUrl = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(checkinUrl)}`;
                  return (
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      <p className="text-sm font-bold text-gray-700 mb-3">
                        Imprime este código y colócalo en la sala del AIP:
                      </p>
                      <img
                        src={qrImgUrl}
                        alt="Código QR de Check-in"
                        className="mx-auto rounded-xl border-4 border-[#1a4d2e]/10 shadow-md"
                        width={260}
                        height={260}
                      />
                      <p className="text-xs text-gray-400 mt-3 break-all">{checkinUrl}</p>
                      <div className="flex flex-wrap gap-3 justify-center mt-4">
                        <Button
                          onClick={() => handleDownloadCheckinQrPdf(checkinUrl)}
                          disabled={isDownloadingQrPdf}
                          className="bg-[#1a4d2e] hover:bg-[#2d6a4f] text-white font-bold px-6 py-3 rounded-xl disabled:opacity-60"
                        >
                          {isDownloadingQrPdf ? '⏳ Generando PDF...' : '📄 Descargar hoja A4 para imprimir'}
                        </Button>
                        <Button
                          onClick={handleRegenerateCheckinCode}
                          disabled={isRegeneratingCode}
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50 font-bold px-6 py-3 rounded-xl disabled:opacity-60"
                        >
                          {isRegeneratingCode ? '⏳ Regenerando...' : '🔄 Regenerar código (invalida QR viejos)'}
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Switch de Estado */}
              <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-gray-100">
                <h3 className="text-xl font-bold text-[#1a4d2e] mb-4">Estado del Sistema de Reservas</h3>
                <div className="flex items-center justify-center gap-6">
                  <p className="text-lg font-medium">
                    El sistema está: <span className={reservationsEnabled ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                      {reservationsEnabled ? "ACTIVO" : "INACTIVO"}
                    </span>
                  </p>
                  <button 
                    onClick={handleToggleReservations}
                    className={`relative inline-flex h-10 w-20 items-center rounded-full transition-colors focus:outline-none ${reservationsEnabled ? 'bg-green-600' : 'bg-red-500'}`}
                  >
                    <span className={`inline-block h-8 w-8 transform rounded-full bg-white transition-transform ${reservationsEnabled ? 'translate-x-11' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>

              {/* Gestión de Mensajes */}
              <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                <h3 className="text-xl font-bold text-[#1a4d2e] mb-4">Mensaje de Mantenimiento</h3>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ejemplo: El sistema estará inactivo por mantenimiento..."
                  className="w-full h-32 p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1a4d2e] text-lg mb-4"
                />
                <Button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/system/update`, { enabled: reservationsEnabled, motivo: motivo.trim() }, axiosConfig);
                      toast.success("Configuración actualizada");
                    } catch (e) { toast.error("Error al guardar"); }
                  }}
                  className="bg-[#1a4d2e] hover:bg-[#2d6a4f] text-white px-8 py-4"
                >
                  Guardar Cambios
                </Button>
              </div>

              {/* Zona de Peligro */}
              {/* --- SECCIÓN DE HERRAMIENTAS TRI-PARTITAS --- */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch mt-8">
                
              {/* CUADRO 1: GESTIÓN DE ADMINS */}
              <Card className="flex flex-col border-2 border-indigo-50 shadow-sm hover:border-indigo-200 transition-all">
                <CardHeader className="bg-indigo-600 text-white rounded-t-lg p-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    👥 Nuevo Administrador
                  </CardTitle>
                </CardHeader>

                <CardContent className="p-5 flex-grow flex flex-col justify-between">
                  {/* AQUÍ ESTÁ EL CAMBIO: Si hay 3 o más, se muestra el mensaje. Si no, el formulario */}
                  {adminList.length >= 3 ? (
                    <div className="flex-grow flex flex-col justify-center items-center space-y-2 text-center py-4">
                      <div className="bg-amber-100 text-amber-600 p-2 rounded-full text-lg">⚠️</div>
                      <p className="text-xs font-bold text-amber-700 uppercase">Límite alcanzado</p>
                      <p className="text-[10px] text-gray-400 italic leading-tight">
                        Ya tienes 3 administradores activos. <br/> Elimina uno para poder agregar otro.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3 mb-4">
                        <Input 
                          value={newAdmin.user}
                          onChange={(e) => setNewAdmin({...newAdmin, user: e.target.value})}
                          placeholder="Usuario"
                          className="h-9 text-sm"
                        />
                        <Input 
                          type="password"
                          value={newAdmin.pass}
                          onChange={(e) => setNewAdmin({...newAdmin, pass: e.target.value})}
                          placeholder="Contraseña"
                          className="h-9 text-sm"
                        />
                      </div>
                      <Button 
                        onClick={handleAddAdmin}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-4"
                      >
                        REGISTRAR ADMIN
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>

                {/* CUADRO 2: RESETEAR CLAVE */}
                <Card className="flex flex-col border-2 border-amber-50 shadow-sm hover:border-amber-200 transition-all">
                  <CardHeader className="bg-amber-500 text-white rounded-t-lg p-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      🔑 Soporte Docente
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 flex-grow flex flex-col justify-between">
                    <div className="space-y-3 mb-4">
                      <p className="text-[11px] text-gray-500 leading-tight">
                        Resetea la clave del docente a <span className="font-bold text-amber-600">123456</span>.
                      </p>
                      <Input 
                        value={dniToReset}
                        onChange={(e) => setDniToReset(e.target.value)}
                        placeholder="DNI del Docente"
                        className="h-9 text-sm"
                      />
                    </div>
                    <Button 
                      onClick={handleResetPassword}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold py-4"
                    >
                      RESETEAR CLAVE
                    </Button>
                  </CardContent>
                </Card>

                {/* CUADRO 3: ELIMINAR RESERVAS */}
                <Card className="flex flex-col border-2 border-red-50 shadow-sm hover:border-red-200 transition-all">
                  <CardHeader className="bg-red-600 text-white rounded-t-lg p-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      🧹 Mantenimiento
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 flex-grow flex flex-col justify-between">
                    <div className="space-y-3 mb-4">
                     <div className="p-6 bg-red-50 border border-red-200 rounded-xl">
                        <h3 className="text-xl font-bold text-red-700 mb-2">Zona de Peligro</h3>
                        <p className="text-red-600 mb-4 text-sm">
                          Esta acción eliminará todas las reservas de la base de datos de forma permanente.
                        </p>
                    </div>
                    <Button 
                      onClick={() => setIsDeleteModalOpen(true)}
                      className="bg-red-600 hover:bg-red-700 text-white font-bold"
                    >
                      🗑️ Borrar Todo
                    </Button>
                  </div>
                    </CardContent>
                    </Card>
              </div>

             {/* --- LISTA DE ADMINISTRADORES (USANDO LAS VARIABLES DECLARADAS) --- */}
              <div className="mt-8 bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-black text-indigo-900 uppercase tracking-widest">
                    Administradores Activos
                  </h3>
                  {/* solo mostramos la longitud de lo que hay en pantalla sobre el máximo de 3 */}
                  <span className="text-[10px] bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full font-bold">
                    {adminList.length} / 3 Cupos Utilizados
                  </span>
                </div>

                {/* --- LISTA ACTUALIZADA Y CORREGIDA --- */}
                <div className="space-y-3">
                  {adminList && adminList.length > 0 ? (
                    adminList.map((admin, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                            {admin.username ? admin.username[0].toUpperCase() : '?'}
                          </div>
                          <span className="text-sm font-bold text-gray-700">{admin.username}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          {/* BOTÓN CAMBIAR CLAVE */}
                          <button
                            onClick={() => handleChangeAdminPass(admin.username)}
                            className="text-[10px] font-bold text-amber-500 hover:bg-amber-100 px-3 py-2 rounded-lg transition-colors flex items-center gap-1 border border-amber-100"
                          >
                            🔑 <span className="hidden md:inline">CLAVE</span>
                          </button>

                          {/* BOTÓN ELIMINAR */}
                          <button
                            onClick={() => handleDeleteAdmin(admin.username)}
                            className="text-[10px] font-bold text-red-500 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors flex items-center gap-1 border border-red-100"
                          >
                            🗑️ <span className="hidden md:inline">ELIMINAR</span>
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-400 text-xs py-4 italic">
                      No hay administradores adicionales registrados.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
  </Tabs>
      </div>

      {/* Modal: confirmar eliminación de una reserva (escribir "eliminar") */}
      {reservationToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
            <h3 className="text-2xl font-black text-gray-900 mb-2">¿Eliminar esta reserva?</h3>
            <p className="text-gray-600 mb-4">
              Esta acción no se puede deshacer. Escribe la palabra{' '}
              <span className="text-red-600 font-black">"eliminar"</span> para confirmar:
            </p>
            <input
              type="text"
              autoFocus
              value={deleteReservationWord}
              onChange={(e) => setDeleteReservationWord(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && deleteReservationWord.trim().toLowerCase() === 'eliminar') confirmDeleteReservation();
              }}
              placeholder='Escribe "eliminar" aquí...'
              className="w-full border-2 border-gray-200 rounded-xl p-3 mb-6 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={closeDeleteReservationModal}
                className="flex-1 border-gray-400 text-gray-700 font-bold"
              >
                CANCELAR
              </Button>
              <Button
                disabled={deleteReservationWord.trim().toLowerCase() !== 'eliminar'}
                onClick={confirmDeleteReservation}
                className={`flex-1 font-bold text-white ${
                  deleteReservationWord.trim().toLowerCase() === 'eliminar'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                ELIMINAR
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de descarga */}
      {isDownloadModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-[#1a4d2e] mb-4">Selecciona nivel, año y meses</h3>

            {/* 🗓️ Selector de año: los meses que se listan abajo son SIEMPRE
                del año elegido aquí, para no mezclar, por ejemplo, "Marzo 2025"
                con "Marzo 2026" en un mismo informe. */}
            {/* 🎓 Selector de nivel: genera el PDF de un solo nivel */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-1">Nivel</label>
              <select
                value={selectedReportLevel}
                onChange={(e) => handleSelectReportLevel(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-2 text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#1a4d2e]"
              >
                <option value="Todos">Todos los niveles (un solo PDF)</option>
                <option value="Inicial">Inicial</option>
                <option value="Primaria">Primaria</option>
                <option value="Secundaria">Secundaria</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-1">Año</label>
              <select
                value={selectedReportYear}
                onChange={(e) => handleSelectReportYear(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-2 text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#1a4d2e]"
              >
                {availableReportYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2 mb-6 max-h-60 overflow-y-auto pr-2">
              <label className="flex items-center space-x-3 cursor-pointer py-2">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 text-[#1a4d2e] rounded focus:ring-[#1a4d2e]"
                />
                <span className="text-gray-800 font-medium">Todos los meses con datos de {selectedReportYear}</span>
              </label>

              <div className="border-t border-gray-200 pt-2">
                {Array.from({ length: 12 }, (_, i) => i + 1).map(monthNum => {
                  const hasData = getMonthsWithDataForYear(selectedReportYear).has(monthNum);
                  return (
                    <label
                      key={monthNum}
                      className={`flex items-center space-x-3 py-1.5 ${hasData ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedMonths.has(monthNum)}
                        disabled={!hasData}
                        onChange={() => toggleMonth(monthNum)}
                        className="h-4 w-4 text-[#1a4d2e] rounded focus:ring-[#1a4d2e]"
                      />
                      <span className="text-gray-700">
                        {getMonthName(monthNum)}{!hasData && ' (sin reservas)'}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={confirmDownloadReport}
                disabled={selectedMonths.size === 0}
                className={`flex-1 ${
                  selectedMonths.size === 0
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-[#1a4d2e] hover:bg-[#2d6a4f]'
                } text-white`}
              >
                Descargar
              </Button>
              <Button
                onClick={closeDownloadModal}
                variant="outline"
                className="flex-1 border-[#1a4d2e] text-[#1a4d2e] hover:bg-[#f5e6d3]"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalles de la Reserva (para que el admin tenga todo listo
          antes de atender al docente en el AIP) */}
      {detailReservation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-8 max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-2xl font-black text-[#1a4d2e]">Detalles de la Reserva</h3>
                <p className="text-gray-500 text-sm font-semibold mt-1">
                  {detailReservation.user_name || 'Docente desconocido'}
                  {detailReservation.user_dni && ` — DNI ${detailReservation.user_dni}`}
                </p>
              </div>
              <button
                onClick={() => setDetailReservation(null)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none font-black"
                title="Cerrar"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase">Fecha</p>
                  <p className="font-bold text-gray-800">
                    {detailReservation.date
                      ? format(parse(detailReservation.date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase">Turno / Horario</p>
                  <p className="font-bold text-gray-800">
                    {detailReservation.shift?.toUpperCase() || '—'}
                    {getHourNumber(detailReservation.shift, detailReservation.time_slot) &&
                      ` (${getHourNumber(detailReservation.shift, detailReservation.time_slot)}H)`}
                    {' — '}{detailReservation.time_slot || '—'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase">Nivel</p>
                  <p className="font-bold text-gray-800">{detailReservation.nivel || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase">Grado / Sección</p>
                  <p className="font-bold text-gray-800">
                    {detailReservation.grado || '—'}{detailReservation.seccion && ` "${detailReservation.seccion}"`}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs font-black text-gray-400 uppercase">Área</p>
                <p className="font-bold text-gray-800">{detailReservation.area || '—'}</p>
              </div>

              <div>
                <p className="text-xs font-black text-gray-400 uppercase">Actividad</p>
                <p className="font-bold text-gray-800">{detailReservation.actividad || '—'}</p>
              </div>

              <div>
                <p className="text-xs font-black text-gray-400 uppercase">Competencia</p>
                <p className="font-bold text-gray-800">{detailReservation.competencia || '—'}</p>
              </div>

              <div>
                <p className="text-xs font-black text-gray-400 uppercase">Recursos Digitales / Tecnológicos</p>
                <p className="font-bold text-gray-800">{detailReservation.recursos_digitales || '—'}</p>
              </div>

              <div>
                <p className="text-xs font-black text-gray-400 uppercase mb-1">Recursos Materiales</p>
                {(detailReservation.recursos_materiales || []).length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {detailReservation.recursos_materiales.map((rec, i) => (
                      <span key={i} className="bg-[#1a4d2e]/10 text-[#1a4d2e] text-xs font-bold px-3 py-1 rounded-full">
                        {rec}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="font-bold text-gray-800">—</p>
                )}
              </div>

              <div>
                <p className="text-xs font-black text-gray-400 uppercase mb-1">Enlaces</p>
                {(detailReservation.enlaces || []).length > 0 ? (
                  <ul className="space-y-1">
                    {detailReservation.enlaces.map((link, i) => (
                      <li key={i}>
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline text-sm break-all font-semibold"
                        >
                          🔗 {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="font-bold text-gray-800">—</p>
                )}
              </div>

              <div>
                <p className="text-xs font-black text-gray-400 uppercase">Asistencia</p>
                {detailReservation.asistio === true ? (
                  <span className="inline-block bg-green-600 text-white text-xs font-black uppercase px-3 py-1 rounded-full mt-1">
                    ✓ Asistió
                  </span>
                ) : detailReservation.asistio === false ? (
                  <span className="inline-block bg-red-600 text-white text-xs font-black uppercase px-3 py-1 rounded-full mt-1">
                    No asistió
                  </span>
                ) : (
                  <span className="inline-block bg-amber-500 text-white text-xs font-black uppercase px-3 py-1 rounded-full mt-1">
                    ⏳ Pendiente
                  </span>
                )}
              </div>
            </div>

            <Button
              onClick={() => setDetailReservation(null)}
              className="w-full mt-6 bg-[#1a4d2e] hover:bg-[#2d6a4f] text-white font-bold"
            >
              Cerrar
            </Button>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 border-t-8 border-red-600 animate-in zoom-in duration-200">
      <div className="text-center mb-6">
        <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">⚠️</span>
        </div>
        <h3 className="text-2xl font-black text-gray-900 mb-2">¿Estás absolutamente seguro?</h3>
        <p className="text-gray-500">
          Esta acción **eliminará permanentemente** todas las reservas del sistema. No se puede deshacer. Este proceso eliminará reservas de años anteriores y del actual. Todo el sistema quedara vacío, sin reservas. ¡CUIDADO!
        </p>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-bold text-gray-700 mb-2 text-center">
          Escribe la palabra <span className="text-red-600 font-black">"borrar"</span> para confirmar:
        </label>
        <input
          type="text"
          value={confirmWord}
          onChange={(e) => setConfirmWord(e.target.value.toLowerCase())}
          placeholder='Escribe "borrar" aquí...'
          className="w-full p-4 border-2 border-gray-200 rounded-xl text-center text-lg font-bold focus:border-red-600 focus:outline-none transition-all uppercase"
        />
      </div>

      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={() => {
            setIsDeleteModalOpen(false);
            setConfirmWord('');
          }}
          className="flex-1 py-6 border-2 font-bold hover:bg-gray-100"
        >
          Cancelar
        </Button>
        <Button
          disabled={confirmWord !== 'borrar'}
          onClick={() => {
            handleDeleteAllReservations(); // Tu función original que borra todo
            setIsDeleteModalOpen(false);
            setConfirmWord('');
          }}
          className={`flex-1 py-6 font-black text-white shadow-lg transition-all ${
            confirmWord === 'borrar' 
              ? 'bg-red-600 hover:bg-red-700 scale-105' 
              : 'bg-gray-300 cursor-not-allowed grayscale'
          }`}
        >
          ¡BORRAR TODO!
        </Button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};
export default AdminDashboard;